import React, { useState, useMemo, useEffect } from 'react';
import {
    Box, Grid, Card, CardContent, Typography, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, Chip, Button, TextField,
    MenuItem, Select, FormControl, InputLabel, Divider, Dialog, DialogTitle,
    DialogContent, DialogActions, IconButton, Tooltip as MuiTooltip,
    Checkbox, FormControlLabel, CircularProgress, Alert, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import PaymentsIcon from '@mui/icons-material/Payments';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import DiscountIcon from '@mui/icons-material/Discount';
import PrintIcon from '@mui/icons-material/Print';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import BarChartIcon from '@mui/icons-material/BarChart';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import CategoryIcon from '@mui/icons-material/Category';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title as ChartTitle, Tooltip, Legend } from 'chart.js';
import { printThermalReceipt } from '../utils/printHelper';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';

// Register ChartJS modules
ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTitle, Tooltip, Legend);

// Global Date Formatting Helper (YYYY-MM-DD)
const getLocalYYYYMMDD = (d = new Date()) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const parseDateSafe = (dateInput) => {
    if (!dateInput) return new Date();
    if (typeof dateInput === 'string') {
        const cleaned = dateInput.trim();
        // If YYYY-MM-DD string, parse at noon (12:00:00) so timezone shifts never change the date
        if (cleaned.length === 10 && cleaned.includes('-')) {
            const [y, m, d] = cleaned.split('-');
            return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10), 12, 0, 0);
        }
        if (cleaned.length >= 19 && cleaned.includes(' ')) {
            const [datePart, timePart] = cleaned.split(' ');
            const [y, m, d] = datePart.split('-');
            const [hh, mm, ss] = timePart.split(':');
            return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10), parseInt(hh, 10) || 0, parseInt(mm, 10) || 0, parseInt(ss, 10) || 0);
        }
    }
    const d = new Date(dateInput);
    return isNaN(d.getTime()) ? new Date() : d;
};

// Pure Date Formatting for Table Visuals (DD-MM-YYYY)
const formatDateDDMMYYYY = (dInput) => {
    if (!dInput) return '-';
    const d = parseDateSafe(dInput);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
};

// Robust 4:00 AM Restaurant Cutoff Calculation
const getEffectiveBusinessDate = (dateObj = new Date(), cutoffHour = 4) => {
    if (typeof dateObj === 'string' && dateObj.length === 10 && dateObj.includes('-')) {
        return dateObj.trim();
    }
    const d = parseDateSafe(dateObj);
    if (d.getHours() < cutoffHour) {
        d.setDate(d.getDate() - 1);
    }
    return getLocalYYYYMMDD(d);
};

// Breakdown Helper for Split & Online Channel Payments (Zomato / Swiggy / Cash / UPI)
const parseOrderPaymentBreakdown = (order) => {
    const total = Number(order.total) || 0;
    const method = String(order.paymentMethod || order.payment_method || '').toLowerCase();
    const source = String(order.order_source || order.orderSource || order.type || '').toLowerCase();

    const isZomato = method.includes('zomato') || source.includes('zomato');
    const isSwiggy = method.includes('swiggy') || source.includes('swiggy');

    if (isZomato) {
        return { cash: 0, upi: 0, zomato: total, swiggy: 0, total, mode: 'zomato' };
    }
    if (isSwiggy) {
        return { cash: 0, upi: 0, zomato: 0, swiggy: total, total, mode: 'swiggy' };
    }
    if (method === 'cash') {
        return { cash: total, upi: 0, zomato: 0, swiggy: 0, total, mode: 'cash' };
    }
    if (method === 'upi' || method === 'online' || method === 'qr') {
        return { cash: 0, upi: total, zomato: 0, swiggy: 0, total, mode: 'upi' };
    }
    if (method.includes('split')) {
        let cashAmt = 0;
        let upiAmt = 0;
        const cashMatch = method.match(/cash:\s*₹?(\d+(\.\d+)?)/i);
        const upiMatch = method.match(/upi:\s*₹?(\d+(\.\d+)?)/i);
        if (cashMatch) cashAmt = parseFloat(cashMatch[1]) || 0;
        if (upiMatch) upiAmt = parseFloat(upiMatch[1]) || 0;
        if (cashAmt === 0 && upiAmt === 0) {
            cashAmt = total; // Fallback
        }
        return { cash: cashAmt, upi: upiAmt, zomato: 0, swiggy: 0, total, mode: 'split' };
    }
    return { cash: total, upi: 0, zomato: 0, swiggy: 0, total, mode: 'cash' };
};

export default function ReportsView() {
    const { orders, categories: appCategories, hotelName, currentUser, tenantSubdomain, accountingLedger, branches, reassignOrderBranch, scanOrdersAudit, applyOrdersFix, forceSettleOrder, syncTenantDataFromDatabase, deleteOrder } = useApp();
    const { currentLanguage } = useLanguage();

    // Auto-sync fresh data on entering ReportsView
    useEffect(() => {
        if (typeof syncTenantDataFromDatabase === 'function') {
            syncTenantDataFromDatabase().catch(err => console.warn("ReportsView mount sync:", err));
        }
    }, []);

    // Dynamic Hotel / Restaurant Name resolution (No Hardcoded values)
    const activeHotelName = (currentUser && currentUser.is_multi_branch && currentUser.branch_name && currentUser.branch_name.toLowerCase() !== 'main branch' ? currentUser.branch_name : (currentUser && currentUser.tenant_name))
        || (currentUser && currentUser.tenant_name)
        || (currentUser && currentUser.name)
        || hotelName
        || localStorage.getItem('dinexa_restaurant_name')
        || localStorage.getItem('dinexa_hotel_name')
        || (tenantSubdomain ? tenantSubdomain.toUpperCase() : 'DINEXA POS');

    // Check if GST is enabled by restaurant owner
    const enableGst = localStorage.getItem('dinexa_gst_enabled') !== 'false';

    // Active Report Selection State (Default: 'performance' - Daily Closing & Performance Matrix)
    const [reportTab, setReportTab] = useState('performance');

    // Common Filter states
    const [dateFilter, setDateFilter] = useState('today');
    const [fromDate, setFromDate] = useState(() => getEffectiveBusinessDate());
    const [toDate, setToDate] = useState(() => getEffectiveBusinessDate());
    const [paymentFilter, setPaymentFilter] = useState('all');
    const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');
    const [selectedBranchFilter, setSelectedBranchFilter] = useState('all');
    const [statementProvider, setStatementProvider] = useState('zomato');

    // Reassign Branch Dialog state
    const [reassignModal, setReassignModal] = useState({ open: false, order: null, targetBranchId: '' });
    const [isReassigning, setIsReassigning] = useState(false);

    // Owner Delete Bill Modal state
    const [deleteBillModal, setDeleteBillModal] = useState({ open: false, order: null });
    const [isDeletingBill, setIsDeletingBill] = useState(false);

    // Owner Force Settle Modal state
    const [settleModal, setSettleModal] = useState({ open: false, order: null, paymentMethod: 'cash' });
    const [isForceSettling, setIsForceSettling] = useState(false);
    const [orderStatusFilter, setOrderStatusFilter] = useState('settled'); // 'settled' | 'stuck'

    // Audit & Fix Modal state (Preview Mode)
    const [auditModal, setAuditModal] = useState({ open: false, issues: [], selectedIds: [] });
    const [isScanning, setIsScanning] = useState(false);
    const [isFixing, setIsFixing] = useState(false);

    // Stuck Orders Count (Unsettled / Cooking KOTs) - strictly scoped by Branch
    const stuckOrdersCount = useMemo(() => {
        let activeBranchId = currentUser?.branch_id;
        if (!activeBranchId) {
            try {
                const u = sessionStorage.getItem('react_pos_current_user');
                if (u) activeBranchId = JSON.parse(u)?.branch_id;
            } catch (e) { }
        }
        if (!activeBranchId) {
            activeBranchId = localStorage.getItem('dinexa_active_branch_id');
        }

        const isOwner = (currentUser?.role === 'owner' || currentUser?.is_owner);
        const branchFilterMode = isOwner ? selectedBranchFilter : 'active';

        return (orders || []).filter(o => {
            const st = String(o.status || '').toLowerCase();
            if (st === 'cancelled' || st === 'void' || st === 'served') return false;
            if (!['cooking', 'preparing', 'pending', 'hold'].includes(st)) return false;

            if (branchFilterMode !== 'all') {
                const targetBId = (branchFilterMode === 'active' || !branchFilterMode) ? activeBranchId : branchFilterMode;
                if (targetBId && o.branch_id && String(o.branch_id) !== String(targetBId)) {
                    return false;
                }
            }
            return true;
        }).length;
    }, [orders, currentUser, selectedBranchFilter]);

    // Column-wise Search Filters for Daily Transactions Ledger (Billing History)
    const [colFilterOrderId, setColFilterOrderId] = useState('');
    const [colFilterTime, setColFilterTime] = useState('');
    const [colFilterType, setColFilterType] = useState('');
    const [colFilterWaiter, setColFilterWaiter] = useState('');
    const [colFilterPayment, setColFilterPayment] = useState('');

    // On-Demand Chart toggle state (Default FALSE for 0% background overhead)
    const [showChart, setShowChart] = useState(false);

    // Closing Receipt Preview Dialog state
    const [openClosingDialog, setOpenClosingDialog] = useState(false);

    // Pure i18n Dictionary mapping (Strict language adherence - No mixed text)
    const isMr = currentLanguage === 'mr';
    const isHi = currentLanguage === 'hi';

    const getBranchName = (bId) => {
        if (!bId) return 'Main Branch';
        const found = (branches || []).find(b => String(b.id) === String(bId));
        return found ? (found.name || found.branch_name || `Branch #${bId}`) : `Branch #${bId}`;
    };

    const handleOpenReassign = (order) => {
        setReassignModal({
            open: true,
            order,
            targetBranchId: order.branch_id || (branches && branches[0] ? branches[0].id : 1)
        });
    };

    const handleConfirmReassign = async () => {
        if (!reassignModal.order || !reassignModal.targetBranchId) return;
        setIsReassigning(true);
        const res = await reassignOrderBranch(reassignModal.order.id, reassignModal.targetBranchId);
        setIsReassigning(false);
        if (res && res.success) {
            setReassignModal({ open: false, order: null, targetBranchId: '' });
        }
    };

    const handleOpenDeleteBill = (order) => {
        setDeleteBillModal({ open: true, order });
    };

    const handleConfirmDeleteBill = async () => {
        if (!deleteBillModal.order) return;
        setIsDeletingBill(true);
        const res = await deleteOrder(deleteBillModal.order.id);
        setIsDeletingBill(false);
        if (res && res.success) {
            setDeleteBillModal({ open: false, order: null });
        }
    };

    const handleOpenForceSettle = (order) => {
        setSettleModal({
            open: true,
            order,
            paymentMethod: order.paymentMethod || order.payment_method || 'cash'
        });
    };

    const handleConfirmForceSettle = async () => {
        if (!settleModal.order) return;
        setIsForceSettling(true);
        const res = await forceSettleOrder(settleModal.order.id, settleModal.paymentMethod);
        setIsForceSettling(false);
        if (res && res.success) {
            setSettleModal({ open: false, order: null, paymentMethod: 'cash' });
        }
    };

    const handleStartScanAudit = async () => {
        setIsScanning(true);
        const data = await scanOrdersAudit();
        setIsScanning(false);
        if (data && data.success) {
            if (!data.issues || data.issues.length === 0) {
                alert(isMr ? '✅ सर्व बिले व KOTs १००% अचूक आहेत. कोणतीही विसंगती किंवा अर्धवट बिल आढळले नाही.' : (isHi ? '✅ सभी बिल और KOT एकदम सही हैं। कोई त्रुटि नहीं मिली।' : '✅ All bills and KOTs are completely accurate. No issues found.'));
            } else {
                setAuditModal({
                    open: true,
                    issues: data.issues,
                    selectedIds: data.issues.map(i => i.order_id)
                });
            }
        }
    };

    const handleToggleSelectOrder = (orderId) => {
        setAuditModal(prev => {
            const exists = prev.selectedIds.includes(orderId);
            const newIds = exists ? prev.selectedIds.filter(id => id !== orderId) : [...prev.selectedIds, orderId];
            return { ...prev, selectedIds: newIds };
        });
    };

    const handleToggleSelectAll = () => {
        setAuditModal(prev => {
            if (prev.selectedIds.length === prev.issues.length) {
                return { ...prev, selectedIds: [] };
            } else {
                return { ...prev, selectedIds: prev.issues.map(i => i.order_id) };
            }
        });
    };

    const handleConfirmApplyFixes = async () => {
        if (!auditModal.selectedIds || auditModal.selectedIds.length === 0) {
            alert(isMr ? 'कृपया दुरुस्तीसाठी किमान एक बिल निवडा.' : (isHi ? 'कृपया सुधार के लिए कम से कम एक बिल चुनें।' : 'Please select at least one order to fix.'));
            return;
        }
        setIsFixing(true);
        const res = await applyOrdersFix(auditModal.selectedIds);
        setIsFixing(false);
        if (res && res.success) {
            setAuditModal({ open: false, issues: [], selectedIds: [] });
        }
    };

    const lbl = {
        title: isMr ? 'विक्री व बंद अहवाल' : (isHi ? 'बिक्री व क्लोजिंग रिपोर्ट' : 'Sales & Closing Reports'),
        subtitle: isMr ? 'दैनिक बंद प्रोग्रेस, व्यवहार रजिस्टर, पदार्थनिहाय आणि कॅटेगिरी विश्लेषण' : (isHi ? 'दैनिक क्लोजिंग प्रोग्रेस, विस्तृत लेन-देन व श्रेणीवार विश्लेषण' : 'Daily closing summaries, transactions audit, item-wise & category analytics'),
        selectReportLabel: isMr ? 'अहवाल प्रकार निवडा' : (isHi ? 'रिपोर्ट प्रकार चुनें' : 'Select Report Type'),

        tabPerformance: isMr ? '📊 १. दैनिक बंद व व्यवसाय प्रोग्रेस अहवाल' : (isHi ? '📊 1. दैनिक क्लोजिंग व व्यवसाय प्रोग्रेस रिपोर्ट' : '📊 1. Daily Closing & Business Performance Matrix'),
        tabDaily: isMr ? '📜 २. दैनिक व्यवहार रजिस्टर' : (isHi ? '📜 2. दैनिक लेन-देन रजिस्टर' : '📜 2. Daily Transactions Ledger'),
        tabItem: isMr ? '🍲 ३. पदार्थनिहाय विक्री अहवाल' : (isHi ? '🍲 3. आइटम-वार बिक्री रिपोर्ट' : '🍲 3. Item-Wise Sales Analytics'),
        tabCategory: isMr ? '📂 ४. कॅटेगिरी अहवाल' : (isHi ? '📂 4. श्रेणीवार बिक्री रिपोर्ट' : '📂 4. Category-Wise Breakdown'),
        tabStaff: isMr ? '👨‍🍳 ५. वेटर व स्टाफ अहवाल' : (isHi ? '👨‍🍳 5. वेटर व स्टाफ रिपोर्ट' : '👨‍🍳 5. Staff Performance Audit'),
        tabPayment: isMr ? '💳 ६. पेमेंट पद्धती व सूट अहवाल' : (isHi ? '💳 6. भुगतान व छूट रिपोर्ट' : '💳 6. Payment Modes & Discount Audit'),
        tabAggregator: isMr ? '🛍️ ७. ऑनलाईन चॅनेल विक्री व पेआउट्स' : (isHi ? '🛍️ 7. ऑनलाइन चैनल बिक्री व पेआउट्स' : '🛍️ 7. Order Channels Sales & Payouts'),
        tabAggregatorStatement: isMr ? '📑 ८. Zomato/Swiggy Statements (PDF)' : (isHi ? '📑 8. Zomato/Swiggy Statements (PDF)' : '📑 8. Zomato/Swiggy Statements (PDF)'),

        dateFilter: isMr ? 'कालावधी निवडा' : (isHi ? 'समय चुनें' : 'Date Filter'),
        today: isMr ? 'आज' : (isHi ? 'आज' : 'Today'),
        yesterday: isMr ? 'काल' : (isHi ? 'कल' : 'Yesterday'),
        week: isMr ? 'चालू आठवडा' : (isHi ? 'इस सप्ताह' : 'This Week'),
        month: isMr ? 'चालू महिना' : (isHi ? 'इस महीने' : 'This Month'),
        custom: isMr ? 'कस्टम तारीख रेंज' : (isHi ? 'कस्टम तारीख' : 'Custom Date Range'),

        paymentFilter: isMr ? 'पेमेंट प्रकार' : (isHi ? 'भुगतान प्रकार' : 'Payment Mode'),
        allPay: isMr ? 'सर्व प्रकार' : (isHi ? 'सभी प्रकार' : 'All Payment Modes'),
        cashPay: isMr ? 'फक्त रोख (Cash)' : (isHi ? 'केवल नकद (Cash)' : 'Cash Only'),
        upiPay: isMr ? 'फक्त ऑनलाईन (UPI)' : (isHi ? 'केवल ऑनलाइन (UPI)' : 'UPI Only'),
        zomatoPay: isMr ? 'फक्त झोमॅटो (Zomato)' : (isHi ? 'केवल जोमैटो (Zomato)' : 'Zomato Only'),
        swiggyPay: isMr ? 'फक्त स्वीगी (Swiggy)' : (isHi ? 'केवल स्वीगी (Swiggy)' : 'Swiggy Only'),
        splitPay: isMr ? 'विभागून दिलेले (Split)' : (isHi ? 'विभाजित (Split)' : 'Split Only'),

        branchFilter: isMr ? 'शाखा निवडा (Branch)' : (isHi ? 'शाखा चुनें' : 'Filter by Branch'),
        allBranches: isMr ? '🏢 सर्व शाखा (All Branches)' : (isHi ? '🏢 सभी शाखाएं' : '🏢 All Branches'),
        activeBranchOnly: isMr ? '📍 सध्याची शाखा (Current Branch)' : (isHi ? '📍 वर्तमान शाखा' : '📍 Current Branch'),

        totalRev: isMr ? 'एकूण महसूल' : (isHi ? 'कुल राजस्व' : 'TOTAL REVENUE'),
        totalRevSub: isMr ? '(केवळ रोख व UPI)' : (isHi ? '(केवल नकद और UPI)' : '(Exclusive of Aggregator Sale)'),
        cashColl: isMr ? 'रोख जमा (Cash)' : (isHi ? 'नकद जमा (Cash)' : 'CASH COLLECTION'),
        upiColl: isMr ? 'ऑनलाईन जमा (UPI)' : (isHi ? 'ऑनलाइन जमा (UPI)' : 'UPI COLLECTION'),
        zomatoColl: isMr ? 'झोमॅटो विक्री (Zomato)' : (isHi ? 'जोमैटो बिक्री (Zomato)' : 'ZOMATO SALES'),
        swiggyColl: isMr ? 'स्वीगी विक्री (Swiggy)' : (isHi ? 'स्वीगी बिक्री (Swiggy)' : 'SWIGGY SALES'),
        totalOrders: isMr ? 'एकूण ऑर्डर्स' : (isHi ? 'कुल ऑर्डर्स' : 'TOTAL BILLS'),
        discounts: isMr ? 'दिलेली सूट' : (isHi ? 'दी गई छूट' : 'DISCOUNTS'),
        cashTag: isMr ? 'गल्ला रोख' : 'Cash',
        upiTag: isMr ? 'ऑनलाईन QR' : 'UPI',
        zomatoTag: 'Zomato',
        swiggyTag: 'Swiggy',
        exportCsv: isMr ? 'डाऊनलोड (CSV)' : (isHi ? 'डाउनलोड (CSV)' : 'Export CSV'),
        closingPrint: isMr ? '🖨️ दैनिक बंद पावती प्रिंट' : (isHi ? '🖨️ दैनिक क्लोजिंग प्रिंट' : '🖨️ Daily Closing Print'),

        // Option A Matrix Section Titles & Headers
        secFinancialTitle: isMr ? 'भाग १: दैनिक जमा-खर्च व निव्वळ विक्री अहवाल (Fund Flow)' : (isHi ? 'भाग 1: दैनिक जमा-खर्च व कुल बिक्री रिपोर्ट (Fund Flow)' : 'Part 1: Daily Financial Closing Matrix (Fund Flow)'),
        secChannelTitle: isMr ? 'भाग २: ऑर्डर चॅनेल विक्री ब्रेक-अप अहवाल' : (isHi ? 'भाग 2: ऑर्डर चैनल बिक्री ब्रेक-अप रिपोर्ट' : 'Part 2: Order Channel Sales Breakdown Matrix'),

        // Group Header Banners (IN vs OUT)
        hdrInGroup: isMr ? '📥 In (जमा रक्कमा / आवक)' : (isHi ? '📥 In (जमा राशि / आवक)' : '📥 In (Cash Inflow & Sales)'),
        hdrOutGroup: isMr ? '📤 Out (खर्च रक्कमा / जावक)' : (isHi ? '📤 Out (खर्च राशि / जावक)' : '📤 Out (Cash Outflow & Expenses)'),

        colDate: isMr ? 'तारीख' : (isHi ? 'तारीख' : 'Date'),
        colBills: isMr ? 'एकूण बिल्स (#)' : (isHi ? 'कुल बिल (#)' : 'Total Bills'),
        colCash: isMr ? 'रोख जमा (₹)' : (isHi ? 'नकद जमा (₹)' : 'Cash Collection (₹)'),
        colUpi: isMr ? 'ऑनलाईन जमा (₹)' : (isHi ? 'ऑनलाइन जमा (₹)' : 'UPI Collection (₹)'),
        colDiscount: isMr ? 'दिलेली सूट (₹)' : (isHi ? 'दी गई छूट (₹)' : 'Discounts (₹)'),
        colTax: isMr ? 'जीएसटी कर ५% (₹)' : (isHi ? 'जीएसटी 5% (₹)' : 'GST Tax 5% (₹)'),
        colGrandTotal: isMr ? 'एकूण विक्री (Grand Total)' : (isHi ? 'कुल बिक्री (Grand Total)' : 'Grand Total Sales'),

        colTotalExpenses: isMr ? 'एकूण खर्च' : (isHi ? 'कुल खर्च' : 'Total Expenses'),
        colCounterCashExp: isMr ? 'गल्ला रोख खर्च' : (isHi ? 'नकद खर्च' : 'Counter Cash Expense'),
        colOnlineExp: isMr ? 'बँक/ऑनलाईन खर्च' : (isHi ? 'बैंक खर्च' : 'Online Expense'),
        colPersonalExp: isMr ? 'ओनर वैयक्तिक खर्च' : (isHi ? 'व्यक्तिगत खर्च' : 'Personal Expense'),
        colNetCashBalance: isMr ? 'निव्वळ गल्ला रोख शिल्लक' : (isHi ? 'शुद्ध नकद शेष' : 'Net Counter Cash Balance'),

        colDineIn: isMr ? 'डायनिंग विक्री (₹)' : (isHi ? 'डाइनिंग बिक्री (₹)' : 'Dine-In Sales (₹)'),
        colZomato: isMr ? 'झोमॅटो (Zomato)' : (isHi ? 'जोमैटो (Zomato)' : 'Zomato Sales'),
        colSwiggy: isMr ? 'स्वीगी (Swiggy)' : (isHi ? 'स्वीगी (Swiggy)' : 'Swiggy Sales'),
        colTakeaway: isMr ? 'पार्सल / डिलिव्हरी' : (isHi ? 'पार्सल / डिलीवरी' : 'Takeaway / Delivery'),
        colTotalChannel: isMr ? 'एकूण विक्री (₹)' : (isHi ? 'कुल बिक्री (₹)' : 'Total Sales (₹)'),
        colTotalSummary: isMr ? 'एकूण बेरीज (Totals)' : (isHi ? 'कुल योग (Totals)' : 'TOTALS'),

        ledgerTitle: isMr ? 'सविस्तर व्यवहार रजिस्टर' : (isHi ? 'विस्तृत लेन-देन रजिस्टर' : 'Detailed Transactions Ledger'),
        colSrNo: isMr ? 'दैनिक बिल क्र. (Sr. No.)' : (isHi ? 'दैनिक बिल क्र. (Sr. No.)' : 'Daily Bill # (Sr. No.)'),
        colOrderId: isMr ? 'ऑर्डर आयडी' : (isHi ? 'ऑर्डर आईडी' : 'Order ID'),
        colBranch: isMr ? 'शाखा' : (isHi ? 'शाखा' : 'Branch'),
        colTime: isMr ? 'वेळ' : (isHi ? 'समय' : 'Time'),
        colType: isMr ? 'प्रकार' : (isHi ? 'प्रकार' : 'Type'),
        colWaiter: isMr ? 'वेटर' : (isHi ? 'वेटर' : 'Waiter'),
        colSubtotal: isMr ? 'मुळ किंमत' : (isHi ? 'उपयोग राशि' : 'Subtotal'),
        colTotal: isMr ? 'एकूण बिल' : (isHi ? 'कुल बिल' : 'Grand Total'),
        colPayment: isMr ? 'पेमेंट प्रकार' : (isHi ? 'भुगतान प्रकार' : 'Payment Mode'),
        colStatus: isMr ? 'स्थिती' : (isHi ? 'स्थिति' : 'Status'),
        noData: isMr ? 'कोणतेही व्यवहार सापडले नाहीत.' : (isHi ? 'कोई भी लेन-देन नहीं मिला।' : 'No transactions found.'),
        showChartBtn: isMr ? '📊 विक्री आलेख उघडा' : (isHi ? '📊 बिक्री चार्ट खोलें' : '📊 Show Sales Chart'),
        hideChartBtn: isMr ? '📊 आलेख लपवा' : (isHi ? '📊 चार्ट छिपाएं' : '📊 Hide Chart'),
        topDishChartTitle: isMr ? 'सर्वाधिक खपणारे १० पदार्थ' : (isHi ? 'शीर्ष 10 सबसे ज्यादा बिकने वाले उत्पाद' : 'Top 10 Best Selling Items'),
        totalItemsSold: isMr ? 'एकूण विकलेले नग' : (isHi ? 'कुल बेचे गए आइटम' : 'TOTAL ITEMS SOLD'),
        topSellingDish: isMr ? 'सर्वोत्तम खपणारा पदार्थ' : (isHi ? 'सबसे ज्यादा बिकने वाला उत्पाद' : 'TOP SELLING DISH'),
        itemRev: isMr ? 'पदार्थांतून मिळालेली रक्कम' : (isHi ? 'आइटम से कुल आय' : 'ITEM REVENUE'),
        colDishName: isMr ? 'पदार्थाचे नाव' : (isHi ? 'आइटम का नाम' : 'Dish Name'),
        colCategory: isMr ? 'कॅटेगिरी' : (isHi ? 'श्रेणी' : 'Category'),
        colPrice: isMr ? 'दर (₹)' : (isHi ? 'मूल्य (₹)' : 'Price (₹)'),
        colQtySold: isMr ? 'विकलेले नग' : (isHi ? 'बेचे गए नग' : 'Qty Sold'),
        colItemRevenue: isMr ? 'एकूण कमाई' : (isHi ? 'कुल कमाई' : 'Total Revenue'),
        colShare: isMr ? 'विक्री वाटा (%)' : (isHi ? 'बिक्री हिस्सा (%)' : 'Sales Share %'),
        paidStatus: isMr ? 'पूर्ण / सेटल' : (isHi ? 'पूर्ण / सेटल' : 'PAID / SETTLED'),
        splitTag: isMr ? 'विभागून दिलेले' : (isHi ? 'विभाजित' : 'SPLIT'),

        totalCatCount: isMr ? 'सक्रिय कॅटेगिरी' : (isHi ? 'सक्रिय श्रेणियां' : 'ACTIVE CATEGORIES'),
        topCatName: isMr ? 'सर्वोत्तम विक्री कॅटेगिरी' : (isHi ? 'शीर्ष बिक्री श्रेणी' : 'TOP SELLING CATEGORY'),
        catChartTitle: isMr ? 'कॅटेगिरीनिहाय विक्री उत्पन्न' : (isHi ? 'श्रेणीवार बिक्री आय' : 'Revenue by Category'),

        totalStaffCount: isMr ? 'काम केलेले वेटर/स्टाफ' : (isHi ? 'कार्यरत वेटर व स्टाफ' : 'ACTIVE WAITERS / STAFF'),
        starStaffName: isMr ? 'नंबर १ वेटर' : (isHi ? 'नंबर 1 वेटर' : 'STAR WAITER'),
        avgOrderVal: isMr ? 'सरासरी बिल रक्कम' : (isHi ? 'औसत बिल राशि' : 'AVG ORDER VALUE'),
        staffChartTitle: isMr ? 'वेटरनिहाय विक्री उत्पन्न' : (isHi ? 'वेटरवार बिक्री आय' : 'Revenue by Waiter'),

        cashRatio: isMr ? 'रोख विक्री वाटा (%)' : (isHi ? 'नकद बिक्री प्रतिशत' : 'CASH SALES SHARE (%)'),
        upiRatio: isMr ? 'ऑनलाईन विक्री वाटा (%)' : (isHi ? 'ऑनलाइन बिक्री प्रतिशत' : 'ONLINE SALES SHARE (%)'),
        avgDiscount: isMr ? 'दर बिलावर सरासरी सूट' : (isHi ? 'औसत छूट प्रति बिल' : 'AVG DISCOUNT / BILL'),
        payChartTitle: isMr ? 'पेमेंट प्रकारनिहाय जमा रक्कम' : (isHi ? 'भुगतान प्रकारवार जमा राशि' : 'Collection by Payment Mode'),
        discountAuditTitle: isMr ? 'दिलेल्या सवलतींचे रजिस्टर (Discount Audit)' : (isHi ? 'दी गई छूट का विवरण (Discount Audit)' : 'Discount Audit & Reason Log'),
        colReason: isMr ? 'सवलतीचे कारण' : (isHi ? 'छूट का कारण' : 'Reason for Discount'),
        btnAuditScan: isMr ? '⚡ ऑर्डर्स व KOTs ऑटो-दुरुस्त करा (Audit & Fix)' : (isHi ? '⚡ ऑर्डर व KOT ऑटो-दुरुस्त करें (Audit & Fix)' : '⚡ Audit & Fix Orders / KOTs'),
        btnCorrectBills: isMr ? 'दुरुस्त करा' : (isHi ? 'दुरुस्त करें' : 'Correct Bills'),
        auditModalTitle: isMr ? '🛠️ ऑर्डर्स व KOTs तपासणी अहवाल (Data Audit & Auto-Fix)' : (isHi ? '🛠️ ऑर्डर व KOT जांच रिपोर्ट (Data Audit & Auto-Fix)' : '🛠️ Orders & KOTs Audit & Auto-Fix'),
    };

    // Filtered orders calculation based on selected Date Range & Payment Method & Branch
    const filteredOrders = useMemo(() => {
        const todayBusinessStr = getEffectiveBusinessDate(new Date(), 4);

        let activeBranchId = currentUser?.branch_id;
        if (!activeBranchId) {
            try {
                const u = sessionStorage.getItem('react_pos_current_user');
                if (u) activeBranchId = JSON.parse(u)?.branch_id;
            } catch (e) { }
        }
        if (!activeBranchId) {
            activeBranchId = localStorage.getItem('dinexa_active_branch_id');
        }

        const isOwner = (currentUser?.role === 'owner' || currentUser?.is_owner);
        const branchFilterMode = isOwner ? selectedBranchFilter : 'active';

        const filtered = orders.filter(o => {
            const st = String(o.status || 'served').toLowerCase();
            if (st === 'cancelled' || st === 'void') {
                return false;
            }

            if (orderStatusFilter === 'stuck') {
                if (!['cooking', 'preparing', 'pending', 'hold'].includes(st)) {
                    return false;
                }
            } else {
                // Default 'settled' mode
                if (['pending', 'cooking', 'preparing', 'hold'].includes(st)) {
                    return false;
                }
            }

            // Branch Filtering
            if (branchFilterMode !== 'all') {
                const targetBId = (branchFilterMode === 'active' || !branchFilterMode) ? activeBranchId : branchFilterMode;
                if (targetBId && o.branch_id && String(o.branch_id) !== String(targetBId)) {
                    return false;
                }
            }

            const rawDate = o.business_date || o.businessDate || ((o.createdAt || o.created_at) ? getEffectiveBusinessDate(o.createdAt || o.created_at, 4) : todayBusinessStr);
            const orderDateStr = String(rawDate).substring(0, 10);

            let dateMatch = true;
            if (dateFilter === 'today') {
                dateMatch = (orderDateStr === todayBusinessStr);
            } else if (dateFilter === 'yesterday') {
                const yest = parseDateSafe(todayBusinessStr);
                yest.setDate(yest.getDate() - 1);
                const yestStr = getLocalYYYYMMDD(yest);
                dateMatch = (orderDateStr === yestStr);
            } else if (dateFilter === 'week') {
                const weekAgo = parseDateSafe(todayBusinessStr);
                weekAgo.setDate(weekAgo.getDate() - 7);
                dateMatch = (orderDateStr >= getLocalYYYYMMDD(weekAgo) && orderDateStr <= todayBusinessStr);
            } else if (dateFilter === 'month') {
                const monthAgo = parseDateSafe(todayBusinessStr);
                monthAgo.setDate(monthAgo.getDate() - 30);
                dateMatch = (orderDateStr >= getLocalYYYYMMDD(monthAgo) && orderDateStr <= todayBusinessStr);
            } else if (dateFilter === 'custom') {
                dateMatch = (orderDateStr >= fromDate && orderDateStr <= toDate);
            }

            let payMatch = true;
            if (paymentFilter !== 'all') {
                const breakdown = parseOrderPaymentBreakdown(o);
                if (paymentFilter === 'cash') {
                    payMatch = (breakdown.mode === 'cash');
                } else if (paymentFilter === 'upi' || paymentFilter === 'online') {
                    payMatch = (breakdown.mode === 'upi');
                } else if (paymentFilter === 'zomato') {
                    payMatch = (breakdown.mode === 'zomato');
                } else if (paymentFilter === 'swiggy') {
                    payMatch = (breakdown.mode === 'swiggy');
                } else if (paymentFilter === 'split') {
                    payMatch = (breakdown.mode === 'split');
                } else {
                    const method = (o.paymentMethod || o.payment_method || '').toLowerCase();
                    payMatch = method.includes(paymentFilter.toLowerCase());
                }
            }

            // Column Filters
            let colMatch = true;
            if (colFilterOrderId && !String(o.id).toLowerCase().includes(colFilterOrderId.toLowerCase())) colMatch = false;
            if (colFilterType && !(o.type || '').toLowerCase().includes(colFilterType.toLowerCase())) colMatch = false;
            if (colFilterWaiter && !(o.waiterName || o.waiter_name || '').toLowerCase().includes(colFilterWaiter.toLowerCase())) colMatch = false;
            if (colFilterPayment && !(o.paymentMethod || o.payment_method || '').toLowerCase().includes(colFilterPayment.toLowerCase())) colMatch = false;
            if (colFilterTime) {
                const timeStr = (o.createdAt || o.created_at) ? parseDateSafe(o.createdAt || o.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }).toLowerCase() : '';
                if (!timeStr.includes(colFilterTime.toLowerCase())) colMatch = false;
            }

            return dateMatch && payMatch && colMatch;
        }).map(o => {
            const sub = Number(o.subtotal) || 0;
            const tx = Number(o.tax) || 0;
            const disc = Number(o.discount) || 0;
            const sc = Number(o.serviceCharge || o.service_charge) || 0;
            const tot = Number(o.total) || 0;

            if (!enableGst) {
                return {
                    ...o,
                    subtotal: sub,
                    tax: 0,
                    discount: disc,
                    serviceCharge: sc,
                    total: Math.max(0, sub + sc - disc)
                };
            }
            return {
                ...o,
                subtotal: sub,
                tax: tx,
                discount: disc,
                serviceCharge: sc,
                total: tot
            };
        });

        // Chronological Ascending Sorting (First bill of the day at the top, latest at the bottom)
        filtered.sort((a, b) => {
            const timeA = (a.createdAt || a.created_at) ? parseDateSafe(a.createdAt || a.created_at).getTime() : 0;
            const timeB = (b.createdAt || b.created_at) ? parseDateSafe(b.createdAt || b.created_at).getTime() : 0;
            if (timeA !== timeB) return timeA - timeB;
            return String(a.id).localeCompare(String(b.id), undefined, { numeric: true, sensitivity: 'base' });
        });

        return filtered;
    }, [orders, dateFilter, fromDate, toDate, paymentFilter, selectedBranchFilter, currentUser, colFilterOrderId, colFilterType, colFilterWaiter, colFilterPayment, colFilterTime, enableGst, orderStatusFilter]);

    // Financial Metrics Calculation (Total Revenue is strictly Cash + UPI)
    const cashSales = useMemo(() => {
        return filteredOrders.reduce((sum, o) => sum + parseOrderPaymentBreakdown(o).cash, 0);
    }, [filteredOrders]);

    const upiSales = useMemo(() => {
        return filteredOrders.reduce((sum, o) => sum + parseOrderPaymentBreakdown(o).upi, 0);
    }, [filteredOrders]);

    const zomatoSales = useMemo(() => {
        return filteredOrders.reduce((sum, o) => sum + parseOrderPaymentBreakdown(o).zomato, 0);
    }, [filteredOrders]);

    const swiggySales = useMemo(() => {
        return filteredOrders.reduce((sum, o) => sum + parseOrderPaymentBreakdown(o).swiggy, 0);
    }, [filteredOrders]);

    const directTotalSales = useMemo(() => (cashSales + upiSales), [cashSales, upiSales]);
    const totalSales = directTotalSales;
    const grossTotalSales = useMemo(() => (cashSales + upiSales + zomatoSales + swiggySales), [cashSales, upiSales, zomatoSales, swiggySales]);
    const totalDiscount = useMemo(() => filteredOrders.reduce((sum, o) => sum + (Number(o.discount) || 0), 0), [filteredOrders]);
    const totalTax = useMemo(() => filteredOrders.reduce((sum, o) => sum + (Number(o.tax) || 0), 0), [filteredOrders]);
    const totalOrdersCount = filteredOrders.length;

    // Performance Matrix Calculation sorted in ASCENDING date order
    const performanceMatrixData = useMemo(() => {
        const dateMap = {};

        // 1. Aggregate Sales Orders by Date
        filteredOrders.forEach(o => {
            const todayStr = getLocalYYYYMMDD();
            const dateKey = o.business_date || o.businessDate || ((o.createdAt || o.created_at) ? getLocalYYYYMMDD(parseDateSafe(o.createdAt || o.created_at)) : todayStr);

            if (!dateMap[dateKey]) {
                dateMap[dateKey] = {
                    date: dateKey,
                    billsCount: 0,
                    cash: 0,
                    upi: 0,
                    discount: 0,
                    tax: 0,
                    dineIn: 0,
                    zomato: 0,
                    swiggy: 0,
                    takeaway: 0,
                    grandTotal: 0,
                    counterCashExpense: 0,
                    onlineExpense: 0,
                    personalExpense: 0,
                    totalExpenses: 0,
                    netCashBalance: 0
                };
            }

            const total = Number(o.total) || 0;
            const discount = Number(o.discount) || 0;
            const tax = Number(o.tax) || 0;

            dateMap[dateKey].billsCount += 1;
            dateMap[dateKey].discount += discount;
            dateMap[dateKey].tax += tax;
            dateMap[dateKey].grandTotal += total;

            const breakdown = parseOrderPaymentBreakdown(o);
            dateMap[dateKey].cash += breakdown.cash;
            dateMap[dateKey].upi += breakdown.upi;

            const source = (o.order_source || o.orderSource || o.type || '').toLowerCase();
            const pay = (o.paymentMethod || o.payment_method || '').toLowerCase();

            if (source.includes('zomato') || pay.includes('zomato')) {
                dateMap[dateKey].zomato += total;
            } else if (source.includes('swiggy') || pay.includes('swiggy')) {
                dateMap[dateKey].swiggy += total;
            } else if (source.includes('takeaway') || source.includes('counter') || source.includes('delivery')) {
                dateMap[dateKey].takeaway += total;
            } else {
                dateMap[dateKey].dineIn += total;
            }
        });

        // 2. Aggregate Expenses by Date & Payment Source (Only within selected date range!)
        if (accountingLedger && Array.isArray(accountingLedger)) {
            const now = new Date();
            const todayStr = now.toISOString().split('T')[0];

            accountingLedger.forEach(exp => {
                const expDateStr = exp.date ? getLocalYYYYMMDD(new Date(exp.date)) : todayStr;

                // Apply the SAME date filter as orders
                let expDateMatch = true;
                if (dateFilter === 'today') {
                    expDateMatch = (expDateStr === todayStr);
                } else if (dateFilter === 'yesterday') {
                    const yest = new Date(now);
                    yest.setDate(now.getDate() - 1);
                    expDateMatch = (expDateStr === getLocalYYYYMMDD(yest));
                } else if (dateFilter === 'week') {
                    const weekAgo = new Date(now);
                    weekAgo.setDate(now.getDate() - 7);
                    expDateMatch = new Date(expDateStr) >= weekAgo;
                } else if (dateFilter === 'month') {
                    const monthAgo = new Date(now);
                    monthAgo.setDate(now.getDate() - 30);
                    expDateMatch = new Date(expDateStr) >= monthAgo;
                } else if (dateFilter === 'custom') {
                    expDateMatch = (expDateStr >= fromDate && expDateStr <= toDate);
                }

                if (!expDateMatch) return; // Skip expenses outside the selected range

                if (!dateMap[expDateStr]) {
                    dateMap[expDateStr] = {
                        date: expDateStr,
                        billsCount: 0,
                        cash: 0,
                        upi: 0,
                        discount: 0,
                        tax: 0,
                        dineIn: 0,
                        zomato: 0,
                        swiggy: 0,
                        takeaway: 0,
                        grandTotal: 0,
                        counterCashExpense: 0,
                        onlineExpense: 0,
                        personalExpense: 0,
                        totalExpenses: 0,
                        netCashBalance: 0
                    };
                }

                const amt = Number(exp.amount) || 0;
                const src = exp.paymentSource || 'counter_cash';

                if (src === 'counter_cash') {
                    dateMap[expDateStr].counterCashExpense += amt;
                } else if (src === 'online') {
                    dateMap[expDateStr].onlineExpense += amt;
                } else if (src === 'personal') {
                    dateMap[expDateStr].personalExpense += amt;
                } else {
                    dateMap[expDateStr].counterCashExpense += amt;
                }

                dateMap[expDateStr].totalExpenses += amt;
            });
        }

        // 3. Compute Net Cash Balance for each date
        Object.values(dateMap).forEach(row => {
            row.netCashBalance = row.cash - row.counterCashExpense;
        });

        const list = Object.values(dateMap);
        // ASCENDING DATE ORDER (Older dates first, latest dates last)
        list.sort((a, b) => new Date(a.date) - new Date(b.date));
        return list;
    }, [filteredOrders, accountingLedger, dateFilter, fromDate, toDate]);

    // Footer Totals for Performance Matrix
    const perfTotals = useMemo(() => {
        return performanceMatrixData.reduce((acc, row) => ({
            billsCount: acc.billsCount + row.billsCount,
            cash: acc.cash + row.cash,
            upi: acc.upi + row.upi,
            discount: acc.discount + row.discount,
            tax: acc.tax + row.tax,
            dineIn: acc.dineIn + row.dineIn,
            zomato: acc.zomato + row.zomato,
            swiggy: acc.swiggy + row.swiggy,
            takeaway: acc.takeaway + row.takeaway,
            grandTotal: acc.grandTotal + row.grandTotal,
            counterCashExpense: acc.counterCashExpense + row.counterCashExpense,
            onlineExpense: acc.onlineExpense + row.onlineExpense,
            personalExpense: acc.personalExpense + row.personalExpense,
            totalExpenses: acc.totalExpenses + row.totalExpenses,
            netCashBalance: acc.netCashBalance + row.netCashBalance
        }), { billsCount: 0, cash: 0, upi: 0, discount: 0, tax: 0, dineIn: 0, zomato: 0, swiggy: 0, takeaway: 0, grandTotal: 0, counterCashExpense: 0, onlineExpense: 0, personalExpense: 0, totalExpenses: 0, netCashBalance: 0 });
    }, [performanceMatrixData]);

    const channelBreakdown = useMemo(() => {
        const zomatoList = filteredOrders.filter(o => o.order_source === 'zomato' || o.orderSource === 'zomato' || (o.type && o.type.toLowerCase().includes('zomato')) || (o.paymentMethod && o.paymentMethod.toLowerCase().includes('zomato')) || (o.payment_method && o.payment_method.toLowerCase().includes('zomato')));
        const swiggyList = filteredOrders.filter(o => o.order_source === 'swiggy' || o.orderSource === 'swiggy' || (o.type && o.type.toLowerCase().includes('swiggy')) || (o.paymentMethod && o.paymentMethod.toLowerCase().includes('swiggy')) || (o.payment_method && o.payment_method.toLowerCase().includes('swiggy')));
        const deliveryList = filteredOrders.filter(o => o.order_source === 'delivery' || o.orderSource === 'delivery' || (o.type && o.type.toLowerCase().includes('delivery')));
        const takeawayList = filteredOrders.filter(o => (o.order_source === 'takeaway' || o.orderSource === 'takeaway' || (o.type && (o.type.toLowerCase().includes('takeaway') || o.type.toLowerCase().includes('counter')))) && !zomatoList.includes(o) && !swiggyList.includes(o) && !deliveryList.includes(o));
        const dineInList = filteredOrders.filter(o => !zomatoList.includes(o) && !swiggyList.includes(o) && !deliveryList.includes(o) && !takeawayList.includes(o));

        return {
            dineIn: { count: dineInList.length, total: dineInList.reduce((sum, o) => sum + (Number(o.total) || 0), 0) },
            zomato: { count: zomatoList.length, total: zomatoList.reduce((sum, o) => sum + (Number(o.total) || 0), 0) },
            swiggy: { count: swiggyList.length, total: swiggyList.reduce((sum, o) => sum + (Number(o.total) || 0), 0) },
            delivery: { count: deliveryList.length, total: deliveryList.reduce((sum, o) => sum + (Number(o.total) || 0), 0) },
            takeaway: { count: takeawayList.length, total: takeawayList.reduce((sum, o) => sum + (Number(o.total) || 0), 0) },
        };
    }, [filteredOrders]);

    // Item-Wise Sales Aggregation
    const itemSalesData = useMemo(() => {
        const itemMap = {};
        filteredOrders.forEach(o => {
            if (o.items && Array.isArray(o.items)) {
                o.items.forEach(item => {
                    const id = item.id;
                    const name = item.name || 'General Dish';
                    const nameMr = item.nameMr || item.name_mr || name;
                    const nameHi = item.nameHi || item.name_hi || name;
                    const cat = item.category || 'General';
                    const catId = item.category_id || 'all';
                    const price = Number(item.price) || 0;
                    const qty = Number(item.quantity) || 1;

                    if (selectedCategoryFilter !== 'all' && catId != selectedCategoryFilter && cat != selectedCategoryFilter) {
                        return;
                    }

                    if (!itemMap[id]) {
                        itemMap[id] = { id, name, nameMr, nameHi, category: cat, price, totalQty: 0, totalRevenue: 0 };
                    }
                    itemMap[id].totalQty += qty;
                    itemMap[id].totalRevenue += (qty * price);
                });
            }
        });

        const list = Object.values(itemMap);
        list.sort((a, b) => b.totalQty - a.totalQty);
        return list;
    }, [filteredOrders, selectedCategoryFilter]);

    const totalItemRevenue = useMemo(() => itemSalesData.reduce((sum, i) => sum + i.totalRevenue, 0), [itemSalesData]);

    // Category-Wise Sales Aggregation
    const categorySalesData = useMemo(() => {
        const catMap = {};
        filteredOrders.forEach(o => {
            if (o.items && Array.isArray(o.items)) {
                o.items.forEach(item => {
                    const cat = item.category || 'General';
                    const qty = item.quantity || 1;
                    const rev = (item.price || 0) * qty;

                    if (!catMap[cat]) {
                        catMap[cat] = { category: cat, totalQty: 0, totalRevenue: 0 };
                    }
                    catMap[cat].totalQty += qty;
                    catMap[cat].totalRevenue += rev;
                });
            }
        });
        const list = Object.values(catMap);
        list.sort((a, b) => b.totalRevenue - a.totalRevenue);
        return list;
    }, [filteredOrders]);

    // Staff Sales Aggregation
    const staffSalesData = useMemo(() => {
        const staffMap = {};
        filteredOrders.forEach(o => {
            const waiter = o.waiterName || 'Counter Staff';
            const total = o.total || 0;

            if (!staffMap[waiter]) {
                staffMap[waiter] = { name: waiter, orderCount: 0, totalRevenue: 0 };
            }
            staffMap[waiter].orderCount += 1;
            staffMap[waiter].totalRevenue += total;
        });
        const list = Object.values(staffMap);
        list.sort((a, b) => b.totalRevenue - a.totalRevenue);
        return list;
    }, [filteredOrders]);

    const paymentModeData = useMemo(() => {
        const pMap = {
            'Cash': { mode: lbl.cashPay, count: 0, amount: 0 },
            'UPI / Online': { mode: lbl.upiPay, count: 0, amount: 0 },
            'Zomato': { mode: lbl.zomatoPay, count: 0, amount: 0 },
            'Swiggy': { mode: lbl.swiggyPay, count: 0, amount: 0 },
            'Split': { mode: lbl.splitPay, count: 0, amount: 0 }
        };

        filteredOrders.forEach(o => {
            const breakdown = parseOrderPaymentBreakdown(o);
            if (breakdown.mode === 'zomato') {
                pMap['Zomato'].count += 1;
                pMap['Zomato'].amount += breakdown.total;
            } else if (breakdown.mode === 'swiggy') {
                pMap['Swiggy'].count += 1;
                pMap['Swiggy'].amount += breakdown.total;
            } else if (breakdown.mode === 'split') {
                pMap['Split'].count += 1;
                pMap['Split'].amount += breakdown.total;
            } else if (breakdown.mode === 'upi') {
                pMap['UPI / Online'].count += 1;
                pMap['UPI / Online'].amount += breakdown.total;
            } else {
                pMap['Cash'].count += 1;
                pMap['Cash'].amount += breakdown.total;
            }
        });

        return Object.values(pMap);
    }, [filteredOrders, lbl]);

    const discountAuditData = useMemo(() => {
        return filteredOrders.filter(o => (o.discount || 0) > 0);
    }, [filteredOrders]);

    // Section PDF Generator (Formatted for A4 Paper with DD-MM-YYYY dates and Page Numbering)
    const handleDownloadSectionPDF = (sectionType) => {
        const now = new Date();
        const generatedDateTimeStr = `${formatDateDDMMYYYY(now)} ${now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`;

        let periodText = '';
        if (dateFilter === 'today') periodText = `${lbl.today} (${formatDateDDMMYYYY(now)})`;
        else if (dateFilter === 'yesterday') {
            const yest = new Date(now);
            yest.setDate(now.getDate() - 1);
            periodText = `${lbl.yesterday} (${formatDateDDMMYYYY(yest)})`;
        }
        else if (dateFilter === 'week') periodText = `${lbl.week} (${formatDateDDMMYYYY(fromDate)} ते ${formatDateDDMMYYYY(toDate)})`;
        else if (dateFilter === 'month') periodText = `${lbl.month} (${formatDateDDMMYYYY(fromDate)} ते ${formatDateDDMMYYYY(toDate)})`;
        else periodText = `${formatDateDDMMYYYY(fromDate)} ते ${formatDateDDMMYYYY(toDate)}`;

        let reportTitle = '';
        let tableHeaderHTML = '';
        let tableRowsHTML = '';
        let tableFooterHTML = '';

        if (sectionType === 'financial') {
            reportTitle = lbl.secFinancialTitle;

            const inColSpan = enableGst ? 5 : 4;
            tableHeaderHTML = `
                <tr>
                    <th colspan="2" style="background-color: #f8fafc !important; border-right: 2.5px solid #64748b !important;"></th>
                    <th colspan="${inColSpan}" style="background-color: #16a34a !important; color: #ffffff !important; font-weight: 900; font-size: 13px; text-align: center; border-right: 2.5px solid #64748b !important;">${lbl.hdrInGroup}</th>
                    <th colspan="4" style="background-color: #dc2626 !important; color: #ffffff !important; font-weight: 900; font-size: 13px; text-align: center;">${lbl.hdrOutGroup}</th>
                </tr>
                <tr>
                    <th>${lbl.colDate}</th>
                    <th style="text-align: center;">${lbl.colBills}</th>
                    <th style="text-align: right;">${lbl.colCash}</th>
                    <th style="text-align: right;">${lbl.colUpi}</th>
                    <th style="text-align: right;">${lbl.colDiscount}</th>
                    ${enableGst ? `<th style="text-align: right;">${lbl.colTax}</th>` : ''}
                    <th style="text-align: right; background-color: #f0fdf4 !important; font-weight: 900; border-right: 2.5px solid #64748b !important;">${lbl.colGrandTotal}</th>
                    <th style="text-align: right; color: #dc2626;">${lbl.colCounterCashExp}</th>
                    <th style="text-align: right; color: #0284c7;">${lbl.colOnlineExp}</th>
                    <th style="text-align: right; color: #92400e;">${lbl.colPersonalExp}</th>
                    <th style="text-align: right; background-color: #f0fdf4 !important; font-weight: 900; color: #047857;">${lbl.colNetCashBalance}</th>
                </tr>
            `;

            if (performanceMatrixData.length === 0) {
                tableRowsHTML = `<tr><td colspan="${enableGst ? 11 : 10}" style="text-align: center; color: #64748b;">${lbl.noData}</td></tr>`;
            } else {
                performanceMatrixData.forEach(row => {
                    tableRowsHTML += `
                        <tr>
                            <td><strong>${formatDateDDMMYYYY(row.date)}</strong></td>
                            <td style="text-align: center;">${row.billsCount}</td>
                            <td style="text-align: right;">₹${row.cash.toFixed(2)}</td>
                            <td style="text-align: right;">₹${row.upi.toFixed(2)}</td>
                            <td style="text-align: right; color: #dc2626;">-₹${row.discount.toFixed(2)}</td>
                            ${enableGst ? `<td style="text-align: right;">₹${row.tax.toFixed(2)}</td>` : ''}
                            <td style="text-align: right; font-weight: 900; background-color: #f0fdf4 !important; border-right: 2.5px solid #64748b !important;">₹${row.grandTotal.toFixed(2)}</td>
                            <td style="text-align: right; color: #dc2626;">-₹${row.counterCashExpense.toFixed(2)}</td>
                            <td style="text-align: right; color: #0284c7;">-₹${row.onlineExpense.toFixed(2)}</td>
                            <td style="text-align: right; color: #92400e;">-₹${row.personalExpense.toFixed(2)}</td>
                            <td style="text-align: right; font-weight: 900; color: #047857; background-color: #f0fdf4 !important;">₹${row.netCashBalance.toFixed(2)}</td>
                        </tr>
                    `;
                });

                tableFooterHTML = `
                    <tr class="totals-row">
                        <td>${lbl.colTotalSummary}</td>
                        <td style="text-align: center;">${perfTotals.billsCount}</td>
                        <td style="text-align: right;">₹${perfTotals.cash.toFixed(2)}</td>
                        <td style="text-align: right;">₹${perfTotals.upi.toFixed(2)}</td>
                        <td style="text-align: right;">-₹${perfTotals.discount.toFixed(2)}</td>
                        ${enableGst ? `<td style="text-align: right;">₹${perfTotals.tax.toFixed(2)}</td>` : ''}
                        <td style="text-align: right; font-weight: 900; border-right: 2.5px solid #64748b !important;">₹${perfTotals.grandTotal.toFixed(2)}</td>
                        <td style="text-align: right; color: #dc2626;">-₹${perfTotals.counterCashExpense.toFixed(2)}</td>
                        <td style="text-align: right; color: #0284c7;">-₹${perfTotals.onlineExpense.toFixed(2)}</td>
                        <td style="text-align: right; color: #92400e;">-₹${perfTotals.personalExpense.toFixed(2)}</td>
                        <td style="text-align: right; font-size: 13px; color: #047857; font-weight: 900;">₹${perfTotals.netCashBalance.toFixed(2)}</td>
                    </tr>
                `;
            }
        } else if (sectionType === 'daily') {
            reportTitle = lbl.ledgerTitle;

            tableHeaderHTML = `
                <tr>
                    <th style="text-align: center; width: 50px;">#</th>
                    <th>${lbl.colOrderId}</th>
                    <th>${lbl.colTime}</th>
                    <th>${lbl.colType}</th>
                    <th>${lbl.colWaiter}</th>
                    <th style="text-align: right;">${lbl.colSubtotal}</th>
                    <th style="text-align: right;">${lbl.colDiscount}</th>
                    ${enableGst ? `<th style="text-align: right;">${lbl.colTax}</th>` : ''}
                    <th style="text-align: right; background-color: #f0fdf4 !important; font-weight: 900;">${lbl.colTotal}</th>
                    <th>${lbl.colPayment}</th>
                </tr>
            `;

            if (filteredOrders.length === 0) {
                tableRowsHTML = `<tr><td colspan="${enableGst ? 10 : 9}" style="text-align: center; color: #64748b;">${lbl.noData}</td></tr>`;
            } else {
                filteredOrders.forEach((o, index) => {
                    const time = (o.createdAt || o.created_at) ? formatDateDDMMYYYY((o.createdAt || o.created_at)) + ' ' + parseDateSafe(o.createdAt || o.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-';
                    tableRowsHTML += `
                        <tr>
                            <td style="text-align: center; font-weight: 900; background-color: #f8fafc;">#${index + 1}</td>
                            <td><strong>${o.id}</strong>${(o.token_number || o.coupon_number) ? `<br/><span style="display:inline-block; margin-top:2px; padding:1px 6px; background:#f0fdf4; color:#16a34a; border-radius:4px; font-size:10px; font-weight:800; border:1px solid #bbf7d0;">🎫 #${o.token_number || o.coupon_number}</span>` : ''}</td>
                            <td>${time}</td>
                            <td>${o.type || 'Table'}</td>
                            <td>${o.waiterName || 'Staff'}</td>
                            <td style="text-align: right;">₹${(o.subtotal || 0).toFixed(2)}</td>
                            <td style="text-align: right; color: #dc2626;">-₹${(o.discount || 0).toFixed(2)}</td>
                            ${enableGst ? `<td style="text-align: right;">₹${(o.tax || 0).toFixed(2)}</td>` : ''}
                            <td style="text-align: right; font-weight: 900; color: #047857; background-color: #f0fdf4 !important;">₹${(o.total || 0).toFixed(2)}</td>
                            <td>${o.paymentMethod || 'Cash'}</td>
                        </tr>
                    `;
                });
            }
        } else if (sectionType === 'channel') {
            reportTitle = lbl.secChannelTitle;

            tableHeaderHTML = `
                <tr>
                    <th>${lbl.colDate}</th>
                    <th style="text-align: right;">${lbl.colDineIn}</th>
                    <th style="text-align: right;">${lbl.colZomato}</th>
                    <th style="text-align: right;">${lbl.colSwiggy}</th>
                    <th style="text-align: right;">${lbl.colTakeaway}</th>
                    <th style="text-align: right;">${lbl.colTotalChannel}</th>
                </tr>
            `;

            if (performanceMatrixData.length === 0) {
                tableRowsHTML = `<tr><td colspan="6" style="text-align: center; color: #64748b;">${lbl.noData}</td></tr>`;
            } else {
                performanceMatrixData.forEach(row => {
                    tableRowsHTML += `
                        <tr>
                            <td><strong>${formatDateDDMMYYYY(row.date)}</strong></td>
                            <td style="text-align: right;">₹${row.dineIn.toFixed(2)}</td>
                            <td style="text-align: right; color: #dc2626;">₹${row.zomato.toFixed(2)}</td>
                            <td style="text-align: right; color: #ea580c;">₹${row.swiggy.toFixed(2)}</td>
                            <td style="text-align: right; color: #0284c7;">₹${row.takeaway.toFixed(2)}</td>
                            <td style="text-align: right; font-weight: 800; color: #047857;">₹${row.grandTotal.toFixed(2)}</td>
                        </tr>
                    `;
                });

                tableFooterHTML = `
                    <tr class="totals-row">
                        <td>${lbl.colTotalSummary}</td>
                        <td style="text-align: right;">₹${perfTotals.dineIn.toFixed(2)}</td>
                        <td style="text-align: right;">₹${perfTotals.zomato.toFixed(2)}</td>
                        <td style="text-align: right;">₹${perfTotals.swiggy.toFixed(2)}</td>
                        <td style="text-align: right;">₹${perfTotals.takeaway.toFixed(2)}</td>
                        <td style="text-align: right; font-size: 13px;">₹${perfTotals.grandTotal.toFixed(2)}</td>
                    </tr>
                `;
            }
        } else if (sectionType === 'aggregator_statement') {
            const providerName = statementProvider === 'zomato' ? 'Zomato' : 'Swiggy';
            reportTitle = `${providerName} Account Statement`;

            tableHeaderHTML = `
                <tr>
                    <th>Date</th>
                    <th>POS Bill ID</th>
                    <th>${providerName} Ref ID</th>
                    <th style="text-align: right;">Amount (₹)</th>
                </tr>
            `;

            const statementOrders = filteredOrders.filter(o =>
                o.order_source === statementProvider ||
                o.orderSource === statementProvider ||
                (o.type && o.type.toLowerCase().includes(statementProvider)) ||
                (o.paymentMethod && o.paymentMethod.toLowerCase().includes(statementProvider)) ||
                (o.payment_method && o.payment_method.toLowerCase().includes(statementProvider))
            ).sort((a, b) => parseDateSafe(a.createdAt || 0) - parseDateSafe(b.createdAt || 0));

            const groupedByDate = {};
            statementOrders.forEach(o => {
                const dateStr = (o.createdAt || o.created_at) ? formatDateDDMMYYYY((o.createdAt || o.created_at)) : 'Unknown';
                if (!groupedByDate[dateStr]) groupedByDate[dateStr] = { count: 0, total: 0, orders: [] };
                groupedByDate[dateStr].orders.push(o);
                groupedByDate[dateStr].count++;
                groupedByDate[dateStr].total += (Number(o.total) || 0);
            });

            let grandTotalCount = 0;
            let grandTotalAmount = 0;

            if (statementOrders.length === 0) {
                tableRowsHTML = `<tr><td colspan="4" style="text-align: center; color: #64748b;">No ${providerName} orders found for the selected period.</td></tr>`;
            } else {
                Object.keys(groupedByDate).forEach(dateStr => {
                    const group = groupedByDate[dateStr];
                    grandTotalCount += group.count;
                    grandTotalAmount += group.total;

                    group.orders.forEach(o => {
                        tableRowsHTML += `
                            <tr>
                                <td>${dateStr}</td>
                                <td>${o.orderNo || o.id}</td>
                                <td>${o.aggregator_id || o.referenceId || '-'}</td>
                                <td style="text-align: right;">₹${(Number(o.total) || 0).toFixed(2)}</td>
                            </tr>
                        `;
                    });

                    tableRowsHTML += `
                        <tr style="background-color: #f1f5f9;">
                            <td colspan="3" style="text-align: right; font-weight: 700; color: #334155;">Date Summary (${dateStr}): ${group.count} Bills</td>
                            <td style="text-align: right; font-weight: 800; color: #0f172a;">₹${group.total.toFixed(2)}</td>
                        </tr>
                    `;
                });

                tableFooterHTML = `
                    <tr class="totals-row">
                        <td colspan="3" style="text-align: right; font-weight: 900;">Grand Total (${grandTotalCount} Bills)</td>
                        <td style="text-align: right; font-weight: 900; font-size: 14px;">₹${grandTotalAmount.toFixed(2)}</td>
                    </tr>
                `;
            }
        }

        const printWin = window.open('', '_blank', 'width=1100,height=800');
        if (!printWin) {
            alert(isMr ? 'कृपया ब्राऊझरचे पॉप-अप (Pop-up) अलाऊ करा!' : 'Please allow popup windows in your browser to generate PDF.');
            return;
        }

        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8" />
                <title>${reportTitle} - ${activeHotelName}</title>
                <style>
                    @page {
                        size: A4 landscape;
                        margin: 15mm;
                        @bottom-left {
                            content: "Print Generated by DinexaPOS";
                            font-family: 'Segoe UI', Roboto, sans-serif;
                            font-size: 8.5pt;
                            font-weight: bold;
                            color: #64748b;
                        }
                        @bottom-right {
                            content: "${isMr ? 'पान क्र.' : (isHi ? 'पृष्ठ संख्या' : 'Page')} " counter(page) " / " counter(pages);
                            font-family: 'Segoe UI', Roboto, sans-serif;
                            font-size: 8.5pt;
                            font-weight: bold;
                            color: #64748b;
                        }
                    }
                    body {
                        font-family: 'Segoe UI', Roboto, -apple-system, sans-serif;
                        color: #0f172a;
                        margin: 0;
                        padding: 30px 40px;
                        background: #ffffff;
                        box-sizing: border-box;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    .pdf-header {
                        border-bottom: 2.5px solid #ea580c;
                        padding-bottom: 8px;
                        margin-bottom: 12px;
                    }
                    .restaurant-brand {
                        font-size: 18px;
                        font-weight: 900;
                        color: #ea580c;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                    }
                    .doc-title {
                        font-size: 13px;
                        font-weight: 800;
                        color: #0f172a;
                        margin-top: 2px;
                    }
                    .meta-block {
                        font-size: 10.5px;
                        color: #475569;
                        text-align: right;
                        line-height: 1.4;
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-top: 8px;
                        font-size: 10.5px;
                        page-break-inside: auto;
                    }
                    tr {
                        page-break-inside: avoid;
                        page-break-after: auto;
                    }
                    th {
                        background-color: #f1f5f9 !important;
                        color: #0f172a;
                        font-weight: 800;
                        padding: 6px 8px;
                        border: 1px solid #cbd5e1;
                    }
                    td {
                        padding: 6px 8px;
                        border: 1px solid #cbd5e1;
                        color: #334155;
                    }
                    tr:nth-child(even) td {
                        background-color: #f8fafc !important;
                    }
                    .totals-row td {
                        background-color: #f0fdf4 !important;
                        font-weight: 900;
                        color: #047857;
                        border-top: 2px solid #059669;
                    }
                </style>
            </head>
            <body>
                <div class="pdf-header">
                    <table style="width: 100%; border: none; margin: 0;">
                        <tr style="background: none;">
                            <td style="border: none; padding: 0;">
                                <div class="restaurant-brand">${activeHotelName}</div>
                                <div class="doc-title">${reportTitle}</div>
                            </td>
                            <td style="border: none; padding: 0; text-align: right;" class="meta-block">
                                <div><strong>${isMr ? 'कालावधी (Period)' : (isHi ? 'अवधि (Period)' : 'Period')}:</strong> ${periodText}</div>
                                <div><strong>${isMr ? 'तयार केल्याची वेळ' : (isHi ? 'समय' : 'Generated')}:</strong> ${generatedDateTimeStr}</div>
                            </td>
                        </tr>
                    </table>
                </div>

                <table>
                    <thead>
                        ${tableHeaderHTML}
                    </thead>
                    <tbody>
                        ${tableRowsHTML}
                    </tbody>
                    ${tableFooterHTML ? `<tfoot>${tableFooterHTML}</tfoot>` : ''}
                </table>

                <script>
                    window.onload = function() {
                        window.print();
                    };
                </script>
            </body>
            </html>
        `;

        printWin.document.open();
        printWin.document.write(htmlContent);
        printWin.document.close();
    };

    // Dynamic CSV Export with Expense Columns
    const handleExportCSV = () => {
        if (reportTab === 'performance') {
            if (performanceMatrixData.length === 0) { alert(lbl.noData); return; }
            let csv = enableGst
                ? `"${lbl.colDate}","${lbl.colBills}","${lbl.colCash}","${lbl.colUpi}","${lbl.colDiscount}","${lbl.colTax}","${lbl.colGrandTotal}","${lbl.colCounterCashExp}","${lbl.colOnlineExp}","${lbl.colPersonalExp}","${lbl.colTotalExpenses}","${lbl.colNetCashBalance}"\n`
                : `"${lbl.colDate}","${lbl.colBills}","${lbl.colCash}","${lbl.colUpi}","${lbl.colDiscount}","${lbl.colGrandTotal}","${lbl.colCounterCashExp}","${lbl.colOnlineExp}","${lbl.colPersonalExp}","${lbl.colTotalExpenses}","${lbl.colNetCashBalance}"\n`;

            performanceMatrixData.forEach(r => {
                const dateDDMM = formatDateDDMMYYYY(r.date);
                csv += enableGst
                    ? `"${dateDDMM}",${r.billsCount},${r.cash.toFixed(2)},${r.upi.toFixed(2)},${r.discount.toFixed(2)},${r.tax.toFixed(2)},${r.grandTotal.toFixed(2)},${r.counterCashExpense.toFixed(2)},${r.onlineExpense.toFixed(2)},${r.personalExpense.toFixed(2)},${r.totalExpenses.toFixed(2)},${r.netCashBalance.toFixed(2)}\n`
                    : `"${dateDDMM}",${r.billsCount},${r.cash.toFixed(2)},${r.upi.toFixed(2)},${r.discount.toFixed(2)},${r.grandTotal.toFixed(2)},${r.counterCashExpense.toFixed(2)},${r.onlineExpense.toFixed(2)},${r.personalExpense.toFixed(2)},${r.totalExpenses.toFixed(2)},${r.netCashBalance.toFixed(2)}\n`;
            });

            csv += enableGst
                ? `"${lbl.colTotalSummary}",${perfTotals.billsCount},${perfTotals.cash.toFixed(2)},${perfTotals.upi.toFixed(2)},${perfTotals.discount.toFixed(2)},${perfTotals.tax.toFixed(2)},${perfTotals.grandTotal.toFixed(2)},${perfTotals.counterCashExpense.toFixed(2)},${perfTotals.onlineExpense.toFixed(2)},${perfTotals.personalExpense.toFixed(2)},${perfTotals.totalExpenses.toFixed(2)},${perfTotals.netCashBalance.toFixed(2)}\n`
                : `"${lbl.colTotalSummary}",${perfTotals.billsCount},${perfTotals.cash.toFixed(2)},${perfTotals.upi.toFixed(2)},${perfTotals.discount.toFixed(2)},${perfTotals.grandTotal.toFixed(2)},${perfTotals.counterCashExpense.toFixed(2)},${perfTotals.onlineExpense.toFixed(2)},${perfTotals.personalExpense.toFixed(2)},${perfTotals.totalExpenses.toFixed(2)},${perfTotals.netCashBalance.toFixed(2)}\n`;

            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Dinexa_Daily_Closing_Performance_Matrix_${getLocalYYYYMMDD()}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            let csv = enableGst
                ? 'Sr. No.,Order ID,Token/Coupon No.,Date & Time,Type,Waiter,Subtotal,Discount,GST Tax,Grand Total,Payment Method\n'
                : 'Sr. No.,Order ID,Token/Coupon No.,Date & Time,Type,Waiter,Subtotal,Discount,Grand Total,Payment Method\n';

            filteredOrders.forEach((o, index) => {
                const time = (o.createdAt || o.created_at) ? formatDateDDMMYYYY((o.createdAt || o.created_at)) + ' ' + parseDateSafe(o.createdAt || o.created_at).toLocaleTimeString() : '';
                const tokenNo = o.token_number || o.coupon_number || o.tokenNumber || o.couponNumber || '-';
                csv += enableGst
                    ? `${index + 1},"${o.id}","${tokenNo}","${time}","${o.type || ''}","${o.waiterName || ''}",${o.subtotal || 0},${o.discount || 0},${o.tax || 0},${o.total || 0},"${o.paymentMethod || 'Cash'}"\n`
                    : `${index + 1},"${o.id}","${tokenNo}","${time}","${o.type || ''}","${o.waiterName || ''}",${o.subtotal || 0},${o.discount || 0},${o.total || 0},"${o.paymentMethod || 'Cash'}"\n`;
            });

            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Dinexa_Sales_Report_${getLocalYYYYMMDD()}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const handlePrintDailyClosing = async () => {
        try {
            window.print();
        } catch (e) {
            console.error("Print error:", e);
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8fafc', minHeight: 'calc(100vh - 70px)' }}>
            {/* Header Banner */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 900, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <BarChartIcon sx={{ color: '#008751', fontSize: 32 }} />
                        {lbl.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block', mt: 0.5 }}>
                        {lbl.subtitle}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    {(currentUser?.role === 'owner' || currentUser?.is_owner) && (
                        <Button
                            variant="outlined"
                            color="warning"
                            onClick={handleStartScanAudit}
                            disabled={isScanning}
                            startIcon={<AutoFixHighIcon />}
                            sx={{ fontWeight: 800, borderRadius: 2.5, borderColor: '#f59e0b', color: '#b45309', bgcolor: '#fffbeb', '&:hover': { bgcolor: '#fef3c7' } }}
                        >
                            {isScanning
                                ? (isMr ? 'तपासत आहे...' : (isHi ? 'जांच हो रही है...' : 'Scanning...'))
                                : lbl.btnAuditScan
                            }
                        </Button>
                    )}
                    <Button
                        variant="outlined"
                        onClick={handleExportCSV}
                        startIcon={<FileDownloadIcon />}
                        sx={{ fontWeight: 800, borderRadius: 2.5, borderColor: '#cbd5e1', color: '#334155', bgcolor: '#ffffff', '&:hover': { bgcolor: '#f8fafc' } }}
                    >
                        {lbl.exportCsv}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => setOpenClosingDialog(true)}
                        startIcon={<PrintIcon />}
                        sx={{ fontWeight: 900, borderRadius: 2.5, bgcolor: '#ea580c', '&:hover': { bgcolor: '#c2410c' } }}
                    >
                        {lbl.closingPrint}
                    </Button>
                </Box>
            </Box>

            {/* Unified Control Bar */}
            <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 3, borderColor: '#e2e8f0', bgcolor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                <Grid container spacing={2} alignItems="center">
                    {/* Filter 1: Select Report Dropdown */}
                    <Grid item xs={12} md={branches && branches.length > 1 ? 3 : 4}>
                        <FormControl size="small" fullWidth>
                            <InputLabel sx={{ fontWeight: 800, color: '#008751' }}>{lbl.selectReportLabel}</InputLabel>
                            <Select
                                value={reportTab}
                                label={lbl.selectReportLabel}
                                onChange={(e) => setReportTab(e.target.value)}
                                sx={{ fontWeight: 900, borderRadius: 2, bgcolor: '#f0fdf4', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#a7f3d0' } }}
                            >
                                <MenuItem value="performance">{lbl.tabPerformance}</MenuItem>
                                <MenuItem value="daily">{lbl.tabDaily}</MenuItem>
                                <MenuItem value="itemWise">{lbl.tabItem}</MenuItem>
                                <MenuItem value="category">{lbl.tabCategory}</MenuItem>
                                <MenuItem value="staff">{lbl.tabStaff}</MenuItem>
                                <MenuItem value="payment">{lbl.tabPayment}</MenuItem>
                                <MenuItem value="aggregator">{lbl.tabAggregator}</MenuItem>
                                <MenuItem value="aggregator_statement">{lbl.tabAggregatorStatement}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Filter 2: Date Filter */}
                    <Grid item xs={12} sm={6} md={dateFilter === 'custom' ? 2 : (branches && branches.length > 1 ? 2.5 : 3)}>
                        <FormControl size="small" fullWidth>
                            <InputLabel sx={{ fontWeight: 700 }}>{lbl.dateFilter}</InputLabel>
                            <Select
                                value={dateFilter}
                                label={lbl.dateFilter}
                                onChange={(e) => setDateFilter(e.target.value)}
                                sx={{ fontWeight: 800, borderRadius: 2 }}
                            >
                                <MenuItem value="today">{lbl.today}</MenuItem>
                                <MenuItem value="yesterday">{lbl.yesterday}</MenuItem>
                                <MenuItem value="week">{lbl.week}</MenuItem>
                                <MenuItem value="month">{lbl.month}</MenuItem>
                                <MenuItem value="custom">{lbl.custom}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {dateFilter === 'custom' && (
                        <>
                            <Grid item xs={6} sm={3} md={2}>
                                <TextField
                                    type="date"
                                    size="small"
                                    fullWidth
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    sx={{ '& input': { py: 0.8, fontSize: '0.85rem' } }}
                                />
                            </Grid>
                            <Grid item xs={6} sm={3} md={2}>
                                <TextField
                                    type="date"
                                    size="small"
                                    fullWidth
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    sx={{ '& input': { py: 0.8, fontSize: '0.85rem' } }}
                                />
                            </Grid>
                        </>
                    )}

                    {/* Filter 3: Payment Filter */}
                    <Grid item xs={12} sm={6} md={dateFilter === 'custom' ? 2 : (branches && branches.length > 1 ? 2.5 : 3)}>
                        <FormControl size="small" fullWidth>
                            <InputLabel sx={{ fontWeight: 700 }}>{lbl.paymentFilter}</InputLabel>
                            <Select
                                value={paymentFilter}
                                label={lbl.paymentFilter}
                                onChange={(e) => setPaymentFilter(e.target.value)}
                                sx={{ fontWeight: 800, borderRadius: 2 }}
                            >
                                <MenuItem value="all">{lbl.allPay}</MenuItem>
                                <MenuItem value="cash">{lbl.cashPay}</MenuItem>
                                <MenuItem value="upi">{lbl.upiPay}</MenuItem>
                                <MenuItem value="zomato">{lbl.zomatoPay}</MenuItem>
                                <MenuItem value="swiggy">{lbl.swiggyPay}</MenuItem>
                                <MenuItem value="split">{lbl.splitPay}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Filter 4: Branch Filter (Multi-Branch Owner Support) */}
                    {(currentUser?.role === 'owner' || currentUser?.is_owner) && branches && branches.length > 1 && (
                        <Grid item xs={12} sm={6} md={dateFilter === 'custom' ? 2 : 3}>
                            <FormControl size="small" fullWidth>
                                <InputLabel sx={{ fontWeight: 700 }}>{lbl.branchFilter}</InputLabel>
                                <Select
                                    value={selectedBranchFilter}
                                    label={lbl.branchFilter}
                                    onChange={(e) => setSelectedBranchFilter(e.target.value)}
                                    sx={{ fontWeight: 800, borderRadius: 2, bgcolor: '#eff6ff' }}
                                >
                                    <MenuItem value="all">{lbl.allBranches}</MenuItem>
                                    <MenuItem value="active">{lbl.activeBranchOnly}</MenuItem>
                                    {branches.map(b => (
                                        <MenuItem key={b.id} value={String(b.id)}>
                                            🏢 {b.name || b.branch_name || `Branch #${b.id}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    )}
                </Grid>
            </Paper>

            {/* High-Level Financial Summary KPI Cards (Separate Cash, UPI, Zomato, Swiggy) */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={2.4}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                        <CardContent sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b', display: 'block' }}>{lbl.totalRev}</Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 700, display: 'block' }}>{lbl.totalRevSub}</Typography>
                                </Box>
                                <Chip label={`${totalOrdersCount} Bills`} size="small" sx={{ fontWeight: 800, bgcolor: '#f0fdf4', color: '#166534', height: 20 }} />
                            </Box>
                            <Typography variant="h5" sx={{ fontWeight: 900, color: '#008751', my: 0.5 }}>
                                ₹{directTotalSales.toFixed(0)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                        <CardContent sx={{ p: 2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b' }}>{lbl.cashColl}</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 900, color: '#16a34a', my: 0.5 }}>
                                ₹{cashSales.toFixed(0)}
                            </Typography>
                            <Chip label={lbl.cashTag} size="small" sx={{ fontWeight: 800, bgcolor: '#f0fdf4', color: '#15803d', height: 20 }} />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                        <CardContent sx={{ p: 2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b' }}>{lbl.upiColl}</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 900, color: '#0284c7', my: 0.5 }}>
                                ₹{upiSales.toFixed(0)}
                            </Typography>
                            <Chip label={lbl.upiTag} size="small" sx={{ fontWeight: 800, bgcolor: '#e0f2fe', color: '#0369a1', height: 20 }} />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #fecaca', bgcolor: '#fef2f2', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                        <CardContent sx={{ p: 2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#cb202d' }}>{lbl.zomatoColl}</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 900, color: '#cb202d', my: 0.5 }}>
                                ₹{zomatoSales.toFixed(0)}
                            </Typography>
                            <Chip label="Zomato" size="small" sx={{ fontWeight: 800, bgcolor: '#cb202d', color: '#ffffff', height: 20 }} />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #fed7aa', bgcolor: '#fff7ed', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                        <CardContent sx={{ p: 2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#ea580c' }}>{lbl.swiggyColl}</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 900, color: '#ea580c', my: 0.5 }}>
                                ₹{swiggySales.toFixed(0)}
                            </Typography>
                            <Chip label="Swiggy" size="small" sx={{ fontWeight: 800, bgcolor: '#fc8019', color: '#ffffff', height: 20 }} />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={4} md={2}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                        <CardContent sx={{ p: 2 }}>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b' }}>{lbl.discounts}</Typography>
                            <Typography variant="h5" sx={{ fontWeight: 900, color: '#ea580c', my: 0.5 }}>
                                -₹{totalDiscount.toFixed(0)}
                            </Typography>
                            <Chip
                                label={enableGst ? `GST: ₹${totalTax.toFixed(0)}` : (isMr ? 'जीएसटी: लागू नाही' : 'GST: Excluded')}
                                size="small"
                                sx={{ fontWeight: 800, bgcolor: enableGst ? '#fff7ed' : '#f1f5f9', color: enableGst ? '#c2410c' : '#64748b', height: 20 }}
                            />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* MAIN REPORT VIEWS RENDERING */}

            {/* REPORT 1: Option A - Daily Closing & Business Performance Matrix */}
            {reportTab === 'performance' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Part 1: Financial Closing Matrix Table */}
                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
                                <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    💵 {lbl.secFinancialTitle}
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="error"
                                    size="small"
                                    onClick={() => handleDownloadSectionPDF('financial')}
                                    startIcon={<PictureAsPdfIcon />}
                                    sx={{ fontWeight: 900, borderRadius: 2, textTransform: 'none', bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}
                                >
                                    {isMr ? 'PDF अहवाल डाऊनलोड' : (isHi ? 'PDF रिपोर्ट डाउनलोड' : 'Download PDF Report')}
                                </Button>
                            </Box>

                            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                                <Table size="medium">
                                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                        {/* Parent Header Banners: IN (Green) vs OUT (Red) with Vertical Divider */}
                                        <TableRow>
                                            <TableCell colSpan={2} align="center" sx={{ bgcolor: '#f8fafc', borderRight: '3px solid #cbd5e1' }}></TableCell>
                                            <TableCell colSpan={enableGst ? 5 : 4} align="center" sx={{ bgcolor: '#16a34a', color: '#ffffff', fontWeight: 900, fontSize: '0.9rem', letterSpacing: 0.5, py: 1, borderRight: '3px solid #cbd5e1' }}>
                                                {lbl.hdrInGroup}
                                            </TableCell>
                                            <TableCell colSpan={4} align="center" sx={{ bgcolor: '#dc2626', color: '#ffffff', fontWeight: 900, fontSize: '0.9rem', letterSpacing: 0.5, py: 1 }}>
                                                {lbl.hdrOutGroup}
                                            </TableCell>
                                        </TableRow>

                                        {/* Column Headers */}
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 900, color: '#1e293b' }}>{lbl.colDate}</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 900, color: '#1e293b' }}>{lbl.colBills}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 900, color: '#16a34a' }}>{lbl.colCash}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 900, color: '#0284c7' }}>{lbl.colUpi}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 900, color: '#ea580c' }}>{lbl.colDiscount}</TableCell>
                                            {enableGst && <TableCell align="right" sx={{ fontWeight: 900, color: '#475569' }}>{lbl.colTax}</TableCell>}
                                            <TableCell align="right" sx={{ fontWeight: 900, color: '#008751', fontSize: '0.95rem', bgcolor: '#f0fdf4', borderRight: '3px solid #cbd5e1' }}>{lbl.colGrandTotal}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 900, color: '#dc2626' }}>{lbl.colCounterCashExp}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 900, color: '#0284c7' }}>{lbl.colOnlineExp}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 900, color: '#92400e' }}>{lbl.colPersonalExp}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 900, color: '#047857', fontSize: '0.95rem', bgcolor: '#f0fdf4' }}>{lbl.colNetCashBalance}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {performanceMatrixData.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={enableGst ? 11 : 10} align="center" sx={{ py: 4, color: '#64748b', fontWeight: 600 }}>
                                                    {lbl.noData}
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            <>
                                                {performanceMatrixData.map((row) => (
                                                    <TableRow key={row.date} hover sx={{ '&:hover': { bgcolor: '#f0fdf4' } }}>
                                                        <TableCell sx={{ fontWeight: 800, color: '#0f172a' }}>{formatDateDDMMYYYY(row.date)}</TableCell>
                                                        <TableCell align="center">
                                                            <Chip label={row.billsCount} size="small" sx={{ fontWeight: 900, bgcolor: '#f1f5f9', color: '#334155' }} />
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 800, color: '#16a34a' }}>₹{row.cash.toFixed(2)}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 800, color: '#0284c7' }}>₹{row.upi.toFixed(2)}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 800, color: '#ea580c' }}>-₹{row.discount.toFixed(2)}</TableCell>
                                                        {enableGst && <TableCell align="right" sx={{ fontWeight: 700, color: '#475569' }}>₹{row.tax.toFixed(2)}</TableCell>}
                                                        <TableCell align="right" sx={{ fontWeight: 900, color: '#0f172a', fontSize: '0.95rem', bgcolor: '#f0fdf4', borderRight: '3px solid #cbd5e1' }}>₹{row.grandTotal.toFixed(2)}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 800, color: '#dc2626' }}>-₹{row.counterCashExpense.toFixed(2)}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 800, color: '#0284c7' }}>-₹{row.onlineExpense.toFixed(2)}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 800, color: '#92400e' }}>-₹{row.personalExpense.toFixed(2)}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 900, color: '#047857', fontSize: '1rem', bgcolor: '#f0fdf4' }}>₹{row.netCashBalance.toFixed(2)}</TableCell>
                                                    </TableRow>
                                                ))}

                                                {/* Summary Totals Row */}
                                                <TableRow sx={{ bgcolor: '#f0fdf4', borderTop: '2px solid #a7f3d0' }}>
                                                    <TableCell sx={{ fontWeight: 900, color: '#047857', fontSize: '0.95rem' }}>{lbl.colTotalSummary}</TableCell>
                                                    <TableCell align="center">
                                                        <Chip label={perfTotals.billsCount} size="small" sx={{ fontWeight: 900, bgcolor: '#008751', color: '#ffffff' }} />
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 900, color: '#16a34a', fontSize: '0.95rem' }}>₹{perfTotals.cash.toFixed(2)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 900, color: '#0284c7', fontSize: '0.95rem' }}>₹{perfTotals.upi.toFixed(2)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 900, color: '#ea580c', fontSize: '0.95rem' }}>-₹{perfTotals.discount.toFixed(2)}</TableCell>
                                                    {enableGst && <TableCell align="right" sx={{ fontWeight: 900, color: '#475569', fontSize: '0.95rem' }}>₹{perfTotals.tax.toFixed(2)}</TableCell>}
                                                    <TableCell align="right" sx={{ fontWeight: 900, color: '#0f172a', fontSize: '1rem', borderRight: '3px solid #cbd5e1' }}>₹{perfTotals.grandTotal.toFixed(2)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 900, color: '#dc2626', fontSize: '0.95rem' }}>-₹{perfTotals.counterCashExpense.toFixed(2)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 900, color: '#0284c7', fontSize: '0.95rem' }}>-₹{perfTotals.onlineExpense.toFixed(2)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 900, color: '#92400e', fontSize: '0.95rem' }}>-₹{perfTotals.personalExpense.toFixed(2)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 900, color: '#047857', fontSize: '1.1rem' }}>₹{perfTotals.netCashBalance.toFixed(2)}</TableCell>
                                                </TableRow>
                                            </>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>

                    {/* Part 2: Order Channel Sales Breakdown Matrix Table */}
                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
                                <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    🛵 {lbl.secChannelTitle}
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="error"
                                    size="small"
                                    onClick={() => handleDownloadSectionPDF('channel')}
                                    startIcon={<PictureAsPdfIcon />}
                                    sx={{ fontWeight: 900, borderRadius: 2, textTransform: 'none', bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}
                                >
                                    {isMr ? 'PDF अहवाल डाऊनलोड' : (isHi ? 'PDF रिपोर्ट डाउनलोड' : 'Download PDF Report')}
                                </Button>
                            </Box>

                            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                                <Table size="medium">
                                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 900, color: '#1e293b' }}>{lbl.colDate}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 900, color: '#16a34a' }}>{lbl.colDineIn}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 900, color: '#dc2626' }}>{lbl.colZomato}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 900, color: '#ea580c' }}>{lbl.colSwiggy}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 900, color: '#0284c7' }}>{lbl.colTakeaway}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 900, color: '#008751', fontSize: '0.95rem' }}>{lbl.colTotalChannel}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {performanceMatrixData.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#64748b', fontWeight: 600 }}>
                                                    {lbl.noData}
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            <>
                                                {performanceMatrixData.map((row) => (
                                                    <TableRow key={row.date} hover sx={{ '&:hover': { bgcolor: '#f0fdf4' } }}>
                                                        <TableCell sx={{ fontWeight: 800, color: '#0f172a' }}>{formatDateDDMMYYYY(row.date)}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 800, color: '#16a34a' }}>₹{row.dineIn.toFixed(2)}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 800, color: '#dc2626' }}>₹{row.zomato.toFixed(2)}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 800, color: '#ea580c' }}>₹{row.swiggy.toFixed(2)}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 800, color: '#0284c7' }}>₹{row.takeaway.toFixed(2)}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 900, color: '#008751', fontSize: '1rem' }}>₹{row.grandTotal.toFixed(2)}</TableCell>
                                                    </TableRow>
                                                ))}

                                                {/* Summary Totals Row */}
                                                <TableRow sx={{ bgcolor: '#f0fdf4', borderTop: '2px solid #a7f3d0' }}>
                                                    <TableCell sx={{ fontWeight: 900, color: '#047857', fontSize: '0.95rem' }}>{lbl.colTotalSummary}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 900, color: '#16a34a', fontSize: '0.95rem' }}>₹{perfTotals.dineIn.toFixed(2)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 900, color: '#dc2626', fontSize: '0.95rem' }}>₹{perfTotals.zomato.toFixed(2)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 900, color: '#ea580c', fontSize: '0.95rem' }}>₹{perfTotals.swiggy.toFixed(2)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 900, color: '#0284c7', fontSize: '0.95rem' }}>₹{perfTotals.takeaway.toFixed(2)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 900, color: '#008751', fontSize: '1.1rem' }}>₹{perfTotals.grandTotal.toFixed(2)}</TableCell>
                                                </TableRow>
                                            </>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Box>
            )}

            {/* REPORT 2: Daily Transactions Ledger */}
            {reportTab === 'daily' && (
                <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                    <CardContent sx={{ p: 3 }}>
                        {/* Stuck Unsettled Orders Alert Banner for Hotel Owner */}
                        {stuckOrdersCount > 0 && (
                            <Alert
                                severity="info"
                                sx={{
                                    mb: 2,
                                    borderRadius: 2,
                                    fontWeight: 700,
                                    bgcolor: '#eff6ff',
                                    color: '#1e40af',
                                    border: '1px solid #bfdbfe',
                                    display: 'flex',
                                    alignItems: 'center',
                                    '& .MuiAlert-icon': { color: '#2563eb' },
                                    '& .MuiAlert-message': { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }
                                }}
                                action={
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <Button
                                            size="small"
                                            variant={orderStatusFilter === 'stuck' ? "contained" : "outlined"}
                                            color="primary"
                                            onClick={() => setOrderStatusFilter(orderStatusFilter === 'stuck' ? 'settled' : 'stuck')}
                                            sx={{ fontWeight: 800, textTransform: 'none', borderRadius: 1.5, borderColor: '#3b82f6' }}
                                        >
                                            {orderStatusFilter === 'stuck'
                                                ? (isMr ? '✅ सर्व पूर्ण झालेली बिले पहा' : 'Show Settled Bills')
                                                : (isMr ? `⏳ ${stuckOrdersCount} चालू (Cooking) KOTs पहा` : `⏳ View ${stuckOrdersCount} Active KOTs`)}
                                        </Button>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="primary"
                                            onClick={handleStartScanAudit}
                                            sx={{ fontWeight: 800, textTransform: 'none', borderRadius: 1.5, bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' } }}
                                        >
                                            {lbl.btnAuditScan}
                                        </Button>
                                    </Box>
                                }
                            >
                                {isMr
                                    ? `ℹ️ आपल्या शाखेत ${stuckOrdersCount} ऑर्डर्स सध्या 'Cooking / Unsettled' स्थितीत चालू आहेत. ग्राहकाचे जेवण झाल्यावर काऊंटरवरून किंवा येथून बिल सेटल करा, म्हणजे ते आपोआप विक्री अहवालात जमा होईल.`
                                    : `ℹ️ ${stuckOrdersCount} order(s) are currently in cooking/unsettled state. Settle them when completed to include in reports.`}
                            </Alert>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                                    📜 {lbl.ledgerTitle}
                                </Typography>

                                {/* Settled vs Stuck KOTs Switcher */}
                                <ToggleButtonGroup
                                    size="small"
                                    value={orderStatusFilter}
                                    exclusive
                                    onChange={(e, newV) => { if (newV) setOrderStatusFilter(newV); }}
                                    sx={{ bgcolor: '#ffffff', borderRadius: 2 }}
                                >
                                    <ToggleButton value="settled" sx={{ fontWeight: 800, textTransform: 'none', px: 1.5, py: 0.5, fontSize: '0.8rem' }}>
                                        ✅ {isMr ? 'पूर्ण झालेली बिले (Settled)' : 'Settled Bills'}
                                    </ToggleButton>
                                    <ToggleButton
                                        value="stuck"
                                        sx={{
                                            fontWeight: 800,
                                            textTransform: 'none',
                                            px: 1.5,
                                            py: 0.5,
                                            fontSize: '0.8rem',
                                            color: stuckOrdersCount > 0 ? '#2563eb' : undefined,
                                            bgcolor: orderStatusFilter === 'stuck' ? '#dbeafe' : undefined
                                        }}
                                    >
                                        ⏳ {isMr ? `चालू KOTs (${stuckOrdersCount})` : `Active KOTs (${stuckOrdersCount})`}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <Button
                                    variant="outlined"
                                    color="success"
                                    size="small"
                                    onClick={handleExportCSV}
                                    startIcon={<FileDownloadIcon />}
                                    sx={{ fontWeight: 900, borderRadius: 2, textTransform: 'none', borderColor: '#16a34a', color: '#16a34a', '&:hover': { bgcolor: '#f0fdf4' } }}
                                >
                                    {isMr ? 'Excel / CSV डाऊनलोड' : (isHi ? 'Excel / CSV डाउनलोड' : 'Download Excel / CSV')}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="error"
                                    size="small"
                                    onClick={() => handleDownloadSectionPDF('daily')}
                                    startIcon={<PictureAsPdfIcon />}
                                    sx={{ fontWeight: 900, borderRadius: 2, textTransform: 'none', bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}
                                >
                                    {isMr ? 'PDF अहवाल डाऊनलोड' : (isHi ? 'PDF रिपोर्ट डाउनलोड' : 'Download PDF Report')}
                                </Button>
                            </Box>
                        </Box>

                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                            <Table size="medium">
                                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 900, width: 85, textAlign: 'center' }}>{lbl.colSrNo}</TableCell>
                                        <TableCell sx={{ fontWeight: 900 }}>{lbl.colOrderId}</TableCell>
                                        {(currentUser?.role === 'owner' || currentUser?.is_owner || (branches && branches.length > 1)) && (
                                            <TableCell sx={{ fontWeight: 900 }}>{lbl.colBranch}</TableCell>
                                        )}
                                        <TableCell sx={{ fontWeight: 900 }}>{lbl.colTime}</TableCell>
                                        <TableCell sx={{ fontWeight: 900 }}>{lbl.colType}</TableCell>
                                        <TableCell sx={{ fontWeight: 900 }}>{lbl.colWaiter}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 900 }}>{lbl.colSubtotal}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 900 }}>{lbl.colDiscount}</TableCell>
                                        {enableGst && <TableCell align="right" sx={{ fontWeight: 900 }}>{lbl.colTax}</TableCell>}
                                        <TableCell align="right" sx={{ fontWeight: 900 }}>{lbl.colTotal}</TableCell>
                                        <TableCell sx={{ fontWeight: 900 }}>{lbl.colPayment}</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 900 }}>{isMr ? 'कृती / पावती' : 'Actions'}</TableCell>
                                    </TableRow>
                                    <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                                        <TableCell sx={{ p: 1 }}></TableCell>
                                        <TableCell sx={{ p: 1 }}><TextField size="small" placeholder={isMr ? "शोध..." : "Search..."} variant="outlined" value={colFilterOrderId} onChange={(e) => setColFilterOrderId(e.target.value)} sx={{ bgcolor: '#ffffff' }} /></TableCell>
                                        {(currentUser?.role === 'owner' || currentUser?.is_owner || (branches && branches.length > 1)) && (
                                            <TableCell sx={{ p: 1 }}></TableCell>
                                        )}
                                        <TableCell sx={{ p: 1 }}><TextField size="small" placeholder={isMr ? "शोध..." : "Search..."} variant="outlined" value={colFilterTime} onChange={(e) => setColFilterTime(e.target.value)} sx={{ bgcolor: '#ffffff' }} /></TableCell>
                                        <TableCell sx={{ p: 1 }}><TextField size="small" placeholder={isMr ? "शोध..." : "Search..."} variant="outlined" value={colFilterType} onChange={(e) => setColFilterType(e.target.value)} sx={{ bgcolor: '#ffffff' }} /></TableCell>
                                        <TableCell sx={{ p: 1 }}><TextField size="small" placeholder={isMr ? "शोध..." : "Search..."} variant="outlined" value={colFilterWaiter} onChange={(e) => setColFilterWaiter(e.target.value)} sx={{ bgcolor: '#ffffff' }} /></TableCell>
                                        <TableCell sx={{ p: 1 }}></TableCell>
                                        <TableCell sx={{ p: 1 }}></TableCell>
                                        {enableGst && <TableCell sx={{ p: 1 }}></TableCell>}
                                        <TableCell sx={{ p: 1 }}></TableCell>
                                        <TableCell sx={{ p: 1 }}><TextField size="small" placeholder={isMr ? "शोध..." : "Search..."} variant="outlined" value={colFilterPayment} onChange={(e) => setColFilterPayment(e.target.value)} sx={{ bgcolor: '#ffffff' }} /></TableCell>
                                        <TableCell sx={{ p: 1 }}></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredOrders.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={enableGst ? 12 : 11} align="center" sx={{ py: 4, color: '#64748b', fontWeight: 600 }}>
                                                {lbl.noData}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredOrders.map((o, index) => (
                                            <TableRow key={o.id} hover>
                                                <TableCell align="center">
                                                    <Chip label={`#${index + 1}`} size="small" sx={{ fontWeight: 900, bgcolor: '#f1f5f9', color: '#0f172a', border: '1px solid #cbd5e1' }} />
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 800, color: '#0f172a' }}>
                                                    <div>{o.id}</div>
                                                    {(o.token_number || o.coupon_number) && (
                                                        <Chip
                                                            label={`🎫 #${o.token_number || o.coupon_number}`}
                                                            size="small"
                                                            sx={{ mt: 0.5, fontWeight: 900, bgcolor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', fontSize: '0.72rem', height: 20 }}
                                                        />
                                                    )}
                                                </TableCell>
                                                {(currentUser?.role === 'owner' || currentUser?.is_owner || (branches && branches.length > 1)) && (
                                                    <TableCell>
                                                        <Chip
                                                            label={getBranchName(o.branch_id)}
                                                            size="small"
                                                            sx={{ fontWeight: 800, bgcolor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' }}
                                                        />
                                                    </TableCell>
                                                )}
                                                <TableCell sx={{ fontSize: '0.8rem', color: '#64748b' }}>
                                                    {(o.createdAt || o.created_at) ? formatDateDDMMYYYY((o.createdAt || o.created_at)) + ' ' + parseDateSafe(o.createdAt || o.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}
                                                </TableCell>
                                                <TableCell><Chip label={o.type || 'Table'} size="small" sx={{ fontWeight: 800, fontSize: '0.75rem' }} /></TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>{o.waiterName || 'Staff'}</TableCell>
                                                <TableCell align="right">₹{(o.subtotal || 0).toFixed(0)}</TableCell>
                                                <TableCell align="right" sx={{ color: '#dc2626', fontWeight: 700 }}>-₹{(o.discount || 0).toFixed(0)}</TableCell>
                                                {enableGst && <TableCell align="right">₹{(o.tax || 0).toFixed(0)}</TableCell>}
                                                <TableCell align="right" sx={{ fontWeight: 900, color: '#008751' }}>₹{(o.total || 0).toFixed(0)}</TableCell>
                                                <TableCell>
                                                    {o.status === 'served' ? (
                                                        <Chip label={o.paymentMethod || 'Cash'} size="small" color="success" variant="outlined" sx={{ fontWeight: 800 }} />
                                                    ) : (
                                                        <Chip
                                                            label={isMr ? '⏳ चालू / Cooking' : (isHi ? '⏳ चालू / Cooking' : '⏳ Cooking')}
                                                            size="small"
                                                            sx={{ fontWeight: 800, bgcolor: '#fef3c7', color: '#b45309', border: '1px solid #fde68a' }}
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center' }}>
                                                        {/* Settle Button (Visible to Manager, Staff, Cashier & Owner on Cooking / Unsettled Orders) */}
                                                        {o.status !== 'served' && (
                                                            <MuiTooltip title={isMr ? "बिल सेटल करा व विक्री अहवालात जमा करा" : "Settle Bill & Add to Sales"}>
                                                                <Button
                                                                    variant="contained"
                                                                    color="success"
                                                                    size="small"
                                                                    onClick={() => handleOpenForceSettle(o)}
                                                                    startIcon={<CheckCircleIcon fontSize="small" />}
                                                                    sx={{ height: 32, fontWeight: 900, textTransform: 'none', bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' }, fontSize: '0.75rem', px: 1 }}
                                                                >
                                                                    {isMr ? 'सेटल करा' : (isHi ? 'सेटल करें' : 'Settle')}
                                                                </Button>
                                                            </MuiTooltip>
                                                        )}

                                                        <Button
                                                            variant="contained"
                                                            color="primary"
                                                            size="small"
                                                            onClick={() => {
                                                                const bObj = (branches || []).find(b => String(b.id) === String(o.branch_id));
                                                                const targetHotelName = bObj?.name || bObj?.branch_name || getBranchName(o.branch_id);
                                                                const orderToPrint = {
                                                                    ...o,
                                                                    hotelName: targetHotelName,
                                                                    branch_name: targetHotelName,
                                                                    branch_address: bObj?.address || bObj?.branch_address,
                                                                    branch_phone: bObj?.phone || bObj?.branch_phone,
                                                                    upi_id: bObj?.upi_id || bObj?.upiId
                                                                };
                                                                printThermalReceipt(orderToPrint, targetHotelName);
                                                            }}
                                                            sx={{ minWidth: 36, width: 36, height: 32, p: 0 }}
                                                        >
                                                            <PrintIcon fontSize="small" />
                                                        </Button>
                                                        {(currentUser?.role === 'owner' || currentUser?.is_owner || (branches && branches.length > 1)) && (
                                                            <MuiTooltip title={isMr ? "शाखा बदला (Reassign Branch)" : "Reassign Branch"}>
                                                                <IconButton
                                                                    size="small"
                                                                    color="warning"
                                                                    onClick={() => handleOpenReassign(o)}
                                                                    sx={{ width: 32, height: 32, bgcolor: '#fef3c7', border: '1px solid #fde68a', '&:hover': { bgcolor: '#fde68a' } }}
                                                                >
                                                                    <SwapHorizIcon fontSize="small" sx={{ color: '#d97706' }} />
                                                                </IconButton>
                                                            </MuiTooltip>
                                                        )}
                                                        {/* Delete / Void Button (Visible to Manager/Staff for unsettled orders, or Owner for any bill) */}
                                                        {(o.status !== 'served' || currentUser?.role === 'owner' || currentUser?.is_owner) && (
                                                            <MuiTooltip title={isMr ? "ऑर्डर / बिल रद्द करा (Delete)" : (isHi ? "बिल डिलीट करें" : "Delete / Cancel")}>
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => handleOpenDeleteBill(o)}
                                                                    sx={{ width: 32, height: 32, bgcolor: '#fee2e2', border: '1px solid #fca5a5', '&:hover': { bgcolor: '#fca5a5' } }}
                                                                >
                                                                    <DeleteIcon fontSize="small" sx={{ color: '#dc2626' }} />
                                                                </IconButton>
                                                            </MuiTooltip>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            {/* REPORT 3: Item-Wise Sales Analytics */}
            {reportTab === 'itemWise' && (
                <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a', mb: 2 }}>
                            🍲 {lbl.tabItem}
                        </Typography>

                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                            <Table size="medium">
                                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 900 }}>{lbl.colDishName}</TableCell>
                                        <TableCell sx={{ fontWeight: 900 }}>{lbl.colCategory}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 900 }}>{lbl.colPrice}</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 900 }}>{lbl.colQtySold}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 900 }}>{lbl.colItemRevenue}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {itemSalesData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#64748b', fontWeight: 600 }}>
                                                {lbl.noData}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        itemSalesData.map(i => (
                                            <TableRow key={i.id} hover>
                                                <TableCell sx={{ fontWeight: 800, color: '#0f172a' }}>{isMr ? i.nameMr : (isHi ? i.nameHi : i.name)}</TableCell>
                                                <TableCell><Chip label={i.category} size="small" sx={{ fontWeight: 700 }} /></TableCell>
                                                <TableCell align="right">₹{i.price}</TableCell>
                                                <TableCell align="center"><Chip label={i.totalQty} size="small" color="primary" sx={{ fontWeight: 900 }} /></TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 900, color: '#008751' }}>₹{i.totalRevenue.toFixed(0)}</TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            {/* REPORT 4: Category Breakdown */}
            {reportTab === 'category' && (
                <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a', mb: 2 }}>
                            📂 {lbl.tabCategory}
                        </Typography>

                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                            <Table size="medium">
                                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 900 }}>{lbl.colCategory}</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 900 }}>{lbl.colQtySold}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 900 }}>{lbl.colTotalRevenue || 'Total Revenue'}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {categorySalesData.map(c => (
                                        <TableRow key={c.category} hover>
                                            <TableCell sx={{ fontWeight: 800, color: '#0f172a' }}>{c.category}</TableCell>
                                            <TableCell align="center"><Chip label={c.totalQty} size="small" sx={{ fontWeight: 900 }} /></TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 900, color: '#008751' }}>₹{c.totalRevenue.toFixed(0)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            {/* REPORT 5: Staff Performance */}
            {reportTab === 'staff' && (
                <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a', mb: 2 }}>
                            👨‍🍳 {lbl.tabStaff}
                        </Typography>

                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                            <Table size="medium">
                                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 900 }}>{lbl.colWaiter}</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 900 }}>{lbl.colBills}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 900 }}>{lbl.colGrandTotal}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {staffSalesData.map(s => (
                                        <TableRow key={s.name} hover>
                                            <TableCell sx={{ fontWeight: 800, color: '#0f172a' }}>{s.name}</TableCell>
                                            <TableCell align="center"><Chip label={s.orderCount} size="small" sx={{ fontWeight: 900 }} /></TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 900, color: '#008751' }}>₹{s.totalRevenue.toFixed(0)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            {/* REPORT 6: Payment Audit */}
            {reportTab === 'payment' && (
                <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a', mb: 2 }}>
                            💳 {lbl.tabPayment}
                        </Typography>

                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                            <Table size="medium">
                                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 900 }}>{lbl.colPayment}</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 900 }}>{lbl.colBills}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 900 }}>{lbl.colGrandTotal}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paymentModeData.map(p => (
                                        <TableRow key={p.mode} hover>
                                            <TableCell sx={{ fontWeight: 800, color: '#0f172a' }}>{p.mode}</TableCell>
                                            <TableCell align="center"><Chip label={p.count} size="small" sx={{ fontWeight: 900 }} /></TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 900, color: '#008751' }}>₹{p.amount.toFixed(0)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            {/* REPORT 7: Order Channel Sales & Payouts */}
            {reportTab === 'aggregator' && (
                <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a', mb: 2 }}>
                            🛍️ {lbl.tabAggregator}
                        </Typography>

                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                            <Table size="medium">
                                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 900, width: 75, textAlign: 'center' }}>#</TableCell>
                                        <TableCell sx={{ fontWeight: 900 }}>{lbl.colOrderId}</TableCell>
                                        <TableCell sx={{ fontWeight: 900 }}>Channel / Source</TableCell>
                                        <TableCell sx={{ fontWeight: 900 }}>Date & Time</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 900 }}>Bill Amount (₹)</TableCell>
                                        <TableCell sx={{ fontWeight: 900 }}>Payment Method</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredOrders.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#64748b', fontWeight: 600 }}>
                                                {lbl.noData}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredOrders.map((o, index) => (
                                            <TableRow key={o.id} hover>
                                                <TableCell align="center">
                                                    <Chip label={`#${index + 1}`} size="small" sx={{ fontWeight: 900, bgcolor: '#f1f5f9', color: '#0f172a', border: '1px solid #cbd5e1' }} />
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 800, color: '#0f172a' }}>{o.id}</TableCell>
                                                <TableCell><Chip label={o.order_source || o.type || 'Dine-In'} size="small" sx={{ fontWeight: 800 }} /></TableCell>
                                                <TableCell sx={{ fontSize: '0.8rem', color: '#64748b' }}>{(o.createdAt || o.created_at) ? formatDateDDMMYYYY((o.createdAt || o.created_at)) + ' ' + parseDateSafe(o.createdAt || o.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 900, color: '#008751' }}>₹{(o.total || 0).toFixed(0)}</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>{o.paymentMethod || 'Settled'}</TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            {/* REPORT 8: Aggregator Statements */}
            {reportTab === 'aggregator_statement' && (
                <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a' }}>
                                {lbl.tabAggregatorStatement}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <FormControl size="small" sx={{ minWidth: 150 }}>
                                    <Select
                                        value={statementProvider}
                                        onChange={(e) => setStatementProvider(e.target.value)}
                                        sx={{ fontWeight: 800, bgcolor: '#f8fafc' }}
                                    >
                                        <MenuItem value="zomato">Zomato</MenuItem>
                                        <MenuItem value="swiggy">Swiggy</MenuItem>
                                    </Select>
                                </FormControl>
                                <Button
                                    variant="contained"
                                    color="error"
                                    startIcon={<PictureAsPdfIcon />}
                                    onClick={() => handleDownloadSectionPDF('aggregator_statement')}
                                    sx={{ fontWeight: 800 }}
                                >
                                    Download PDF
                                </Button>
                            </Box>
                        </Box>

                        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
                            <Table size="medium">
                                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 900, width: 75, textAlign: 'center' }}>#</TableCell>
                                        <TableCell sx={{ fontWeight: 900 }}>Date</TableCell>
                                        <TableCell sx={{ fontWeight: 900 }}>POS Bill ID</TableCell>
                                        <TableCell sx={{ fontWeight: 900 }}>{statementProvider === 'zomato' ? 'Zomato' : 'Swiggy'} Ref ID</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 900 }}>Bill Amount (₹)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredOrders.filter(o =>
                                        o.order_source === statementProvider ||
                                        o.orderSource === statementProvider ||
                                        (o.type && o.type.toLowerCase().includes(statementProvider)) ||
                                        (o.paymentMethod && o.paymentMethod.toLowerCase().includes(statementProvider)) ||
                                        (o.payment_method && o.payment_method.toLowerCase().includes(statementProvider))
                                    ).length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#64748b', fontWeight: 600 }}>
                                                {lbl.noData || 'No orders found for selected period.'}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredOrders.filter(o =>
                                            o.order_source === statementProvider ||
                                            o.orderSource === statementProvider ||
                                            (o.type && o.type.toLowerCase().includes(statementProvider)) ||
                                            (o.paymentMethod && o.paymentMethod.toLowerCase().includes(statementProvider)) ||
                                            (o.payment_method && o.payment_method.toLowerCase().includes(statementProvider))
                                        ).map((o, index) => (
                                            <TableRow key={o.id} hover>
                                                <TableCell align="center">
                                                    <Chip label={`#${index + 1}`} size="small" sx={{ fontWeight: 900, bgcolor: '#f1f5f9', color: '#0f172a', border: '1px solid #cbd5e1' }} />
                                                </TableCell>
                                                <TableCell sx={{ fontSize: '0.85rem', color: '#475569' }}>
                                                    {(o.createdAt || o.created_at) ? formatDateDDMMYYYY((o.createdAt || o.created_at)) : '-'}
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 800, color: '#0f172a' }}>{o.orderNo || o.id}</TableCell>
                                                <TableCell sx={{ color: '#ea580c', fontWeight: 700 }}>{o.aggregator_id || o.referenceId || '-'}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 900, color: '#008751' }}>₹{(o.total || 0).toFixed(0)}</TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            {/* Daily Closing Summary Receipt Dialog */}
            <Dialog open={openClosingDialog} onClose={() => setOpenClosingDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
                <DialogTitle sx={{ fontWeight: 900, textAlign: 'center', bgcolor: '#ea580c', color: '#ffffff', py: 2 }}>
                    🖨️ Daily Closing Summary
                </DialogTitle>
                <DialogContent sx={{ p: 3, bgcolor: '#ffffff' }}>
                    <Box sx={{ textTransform: 'uppercase', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 900, textAlign: 'center', mb: 0.5 }}>
                            {activeHotelName}
                        </Typography>
                        <Typography variant="caption" display="block" align="center" sx={{ color: '#64748b', mb: 2 }}>
                            Date: {formatDateDDMMYYYY(new Date())} | Time: {new Date().toLocaleTimeString()}
                        </Typography>
                        <Divider sx={{ my: 1, borderStyle: 'dashed' }} />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 0.8 }}>
                            <span>TOTAL BILLS SETTLED:</span>
                            <strong>{totalOrdersCount}</strong>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 0.8 }}>
                            <span>CASH COLLECTION:</span>
                            <strong>₹{cashSales.toFixed(2)}</strong>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 0.8 }}>
                            <span>ONLINE / UPI COLLECTION:</span>
                            <strong>₹{upiSales.toFixed(2)}</strong>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 0.8 }}>
                            <span>TOTAL DISCOUNTS GIVEN:</span>
                            <strong>-₹{totalDiscount.toFixed(2)}</strong>
                        </Box>

                        {enableGst && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 0.8 }}>
                                <span>GST TAX COLLECTED (5%):</span>
                                <strong>₹{totalTax.toFixed(2)}</strong>
                            </Box>
                        )}

                        <Divider sx={{ my: 1, borderStyle: 'dashed' }} />
                        <Typography variant="caption" sx={{ fontWeight: 900, display: 'block', mb: 0.5, color: '#334155', textAlign: 'center' }}>
                            --- CHANNEL BREAKDOWN ---
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 0.4 }}>
                            <span>🍽️ DINE-IN ({channelBreakdown.dineIn.count}):</span>
                            <strong>₹{channelBreakdown.dineIn.total.toFixed(2)}</strong>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 0.4 }}>
                            <span>🔴 ZOMATO ({channelBreakdown.zomato.count}):</span>
                            <strong>₹{channelBreakdown.zomato.total.toFixed(2)}</strong>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 0.4 }}>
                            <span>🟠 SWIGGY ({channelBreakdown.swiggy.count}):</span>
                            <strong>₹{channelBreakdown.swiggy.total.toFixed(2)}</strong>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 0.4 }}>
                            <span>🛵 DELIVERY ({channelBreakdown.delivery.count}):</span>
                            <strong>₹{channelBreakdown.delivery.total.toFixed(2)}</strong>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 0.4 }}>
                            <span>📦 TAKEAWAY ({channelBreakdown.takeaway.count}):</span>
                            <strong>₹{channelBreakdown.takeaway.total.toFixed(2)}</strong>
                        </Box>

                        <Divider sx={{ my: 1, borderStyle: 'dashed' }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 1, fontSize: '1rem', fontWeight: 900 }}>
                            <span>GRAND TOTAL DAY SALES:</span>
                            <span>₹{totalSales.toFixed(2)}</span>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#f8fafc' }}>
                    <Button onClick={() => setOpenClosingDialog(false)} sx={{ fontWeight: 700, color: '#64748b' }}>
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handlePrintDailyClosing}
                        sx={{ bgcolor: '#ea580c', '&:hover': { bgcolor: '#c2410c' }, fontWeight: 900, borderRadius: 2 }}
                    >
                        🖨️ Print Receipt
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reassign Branch Modal */}
            <Dialog
                open={reassignModal.open}
                onClose={() => setReassignModal({ open: false, order: null, targetBranchId: '' })}
                PaperProps={{ sx: { borderRadius: 3, p: 1, maxWidth: 450, width: '100%' } }}
            >
                <DialogTitle sx={{ fontWeight: 900, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SwapHorizIcon sx={{ color: '#ea580c' }} />
                    {isMr ? `ऑर्डर #${reassignModal.order?.id} ची शाखा बदला` : `Reassign Branch for Order #${reassignModal.order?.id}`}
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                        {isMr ? 'जर हे बिल चुकीच्या शाखेत सेव्ह झाले असेल तर खालील शाखेची निवड करून त्वरित ट्रान्सफर करा:' : 'If this order was punched in the wrong branch, select the target branch to transfer it:'}
                    </Typography>

                    <Box sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: 2, mb: 2.5, border: '1px solid #e2e8f0' }}>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b', display: 'block', mb: 0.5 }}>
                            {isMr ? 'सध्याची शाखा:' : 'Current Branch:'} <strong>{getBranchName(reassignModal.order?.branch_id)}</strong>
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b', display: 'block', mb: 0.5 }}>
                            {isMr ? 'वेटर / काउंटर:' : 'Staff / Waiter:'} <strong>{reassignModal.order?.waiterName || 'Staff'}</strong>
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#008751', display: 'block' }}>
                            {isMr ? 'एकूण रक्कम:' : 'Bill Amount:'} <strong>₹{(reassignModal.order?.total || 0).toFixed(0)}</strong>
                        </Typography>
                    </Box>

                    <FormControl fullWidth size="small">
                        <InputLabel sx={{ fontWeight: 800 }}>{isMr ? 'नवीन शाखा निवडा' : 'Select Target Branch'}</InputLabel>
                        <Select
                            value={reassignModal.targetBranchId}
                            label={isMr ? 'नवीन शाखा निवडा' : 'Select Target Branch'}
                            onChange={(e) => setReassignModal(prev => ({ ...prev, targetBranchId: e.target.value }))}
                            sx={{ fontWeight: 800, borderRadius: 2 }}
                        >
                            {(branches || []).map(b => (
                                <MenuItem key={b.id} value={b.id}>
                                    🏢 {b.name || b.branch_name || `Branch #${b.id}`}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={() => setReassignModal({ open: false, order: null, targetBranchId: '' })}
                        sx={{ fontWeight: 800, color: '#64748b' }}
                    >
                        {isMr ? 'रद्द करा' : 'Cancel'}
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleConfirmReassign}
                        disabled={isReassigning}
                        sx={{ fontWeight: 900, borderRadius: 2, bgcolor: '#ea580c', '&:hover': { bgcolor: '#c2410c' } }}
                    >
                        {isReassigning ? (isMr ? 'बदलत आहे...' : 'Transferring...') : (isMr ? 'शाखा बदला' : 'Confirm Transfer')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Comprehensive Audit & Fix Modal (Preview Mode) */}
            <Dialog
                open={auditModal.open}
                onClose={() => !isFixing && setAuditModal({ open: false, issues: [], selectedIds: [] })}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 900, color: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <AutoFixHighIcon sx={{ color: '#ea580c', fontSize: 28 }} />
                        <Typography variant="h6" sx={{ fontWeight: 900, color: '#0f172a' }}>
                            {lbl.auditModalTitle}
                        </Typography>
                    </Box>
                    <Chip
                        label={`${auditModal.issues.length} ${isMr ? 'त्रुटी सापडल्या' : (isHi ? 'त्रुटियां मिलीं' : 'Issues Found')}`}
                        color="warning"
                        size="small"
                        sx={{ fontWeight: 800 }}
                    />
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Typography variant="body2" sx={{ color: '#475569', mb: 2, fontWeight: 500 }}>
                        {isMr
                            ? 'खालील बिले/KOTs मध्ये शाखा विसंगती, अर्धवट राहिलेले KOTs किंवा रक्कमेच्या त्रुटी आढळल्या आहेत. आपण तपासणी करून "दुरुस्त करा" बटनावर क्लिक केल्यावर सिस्टीम सर्व दुरुस्त्या सुरक्षितपणे लागू करेल:'
                            : (isHi
                                ? 'नीचे दिए गए बिलों/KOTs में शाखा विसंगति, अधूरे KOTs या राशि की त्रुटियां पाई गई हैं। जांच के बाद "दुरुस्त करें" पर क्लिक करें:'
                                : 'The following orders have branch mismatches, unsettled stuck KOTs, or missing amounts. Review and click "Correct Bills" to fix them:')
                        }
                    </Typography>

                    {/* Bulk Selection Bar */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f1f5f9', p: 1.5, borderRadius: 2, mb: 2, border: '1px solid #e2e8f0' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={auditModal.selectedIds.length === auditModal.issues.length && auditModal.issues.length > 0}
                                    indeterminate={auditModal.selectedIds.length > 0 && auditModal.selectedIds.length < auditModal.issues.length}
                                    onChange={handleToggleSelectAll}
                                    color="warning"
                                />
                            }
                            label={<Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e293b' }}>{isMr ? 'सर्व बिले निवडा (Select All)' : (isHi ? 'सभी बिल चुनें' : 'Select All')}</Typography>}
                        />
                        <Typography variant="caption" sx={{ fontWeight: 800, color: '#008751' }}>
                            {auditModal.selectedIds.length} / {auditModal.issues.length} {isMr ? 'निवडलेली बिले' : (isHi ? 'चयनित बिल' : 'selected')}
                        </Typography>
                    </Box>

                    {/* Issues Table */}
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 380 }}>
                        <Table size="small" stickyHeader>
                            <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                <TableRow>
                                    <TableCell sx={{ width: 40 }}></TableCell>
                                    <TableCell sx={{ fontWeight: 900 }}>{lbl.colOrderId}</TableCell>
                                    <TableCell sx={{ fontWeight: 900 }}>{isMr ? 'त्रुटीचा प्रकार' : (isHi ? 'त्रुटि का प्रकार' : 'Issue Type')}</TableCell>
                                    <TableCell sx={{ fontWeight: 900 }}>{isMr ? 'सध्याची स्थिती' : (isHi ? 'वर्तमान स्थिति' : 'Current State')}</TableCell>
                                    <TableCell sx={{ fontWeight: 900 }}>{isMr ? 'प्रस्तावित दुरुस्ती' : (isHi ? 'प्रस्तावित सुधार' : 'Proposed Fix')}</TableCell>
                                    <TableCell sx={{ fontWeight: 900 }}>{isMr ? 'तपशील / कारण' : (isHi ? 'विवरण / कारण' : 'Reason / Details')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {auditModal.issues.map((item) => {
                                    const isSelected = auditModal.selectedIds.includes(item.order_id);
                                    return (
                                        <TableRow key={item.order_id} hover selected={isSelected} sx={{ '&.Mui-selected': { bgcolor: '#fffbeb' } }}>
                                            <TableCell>
                                                <Checkbox
                                                    checked={isSelected}
                                                    onChange={() => handleToggleSelectOrder(item.order_id)}
                                                    size="small"
                                                    color="warning"
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 800, color: '#0f172a', whiteSpace: 'nowrap' }}>
                                                {item.order_id}
                                                <Typography variant="caption" sx={{ display: 'block', color: '#64748b', fontSize: '0.7rem' }}>
                                                    {formatDateDDMMYYYY(item.created_at)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    {item.issues.includes('branch_mismatch') && (
                                                        <Chip label={isMr ? "🏢 शाखा बदल" : (isHi ? "🏢 शाखा बदलें" : "Branch")} size="small" color="warning" sx={{ fontWeight: 800, fontSize: '0.65rem', height: 20 }} />
                                                    )}
                                                    {item.issues.includes('stuck_unsettled') && (
                                                        <Chip label={isMr ? "⏳ अर्धवट KOT" : (isHi ? "⏳ अधूरा KOT" : "Stuck KOT")} size="small" color="error" sx={{ fontWeight: 800, fontSize: '0.65rem', height: 20 }} />
                                                    )}
                                                    {item.issues.includes('missing_totals') && (
                                                        <Chip label={isMr ? "🔢 बेरीज दुरुस्ती" : (isHi ? "🔢 योग सुधार" : "Missing Total")} size="small" color="secondary" sx={{ fontWeight: 800, fontSize: '0.65rem', height: 20 }} />
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.8rem', color: '#64748b' }}>
                                                <div><strong>शाखा:</strong> {item.current_branch_name}</div>
                                                <div><strong>स्थिती:</strong> {item.current_status} (₹{item.current_total.toFixed(0)})</div>
                                                <div><strong>स्टाफ:</strong> {item.waiter_name}</div>
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.8rem', color: '#008751', fontWeight: 700 }}>
                                                <div>🏢 <strong>नवीन शाखा:</strong> {item.proposed_branch_name}</div>
                                                <div>✅ <strong>स्थिती:</strong> {item.proposed_status} (₹{item.proposed_total.toFixed(0)})</div>
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.75rem', color: '#475569', maxWidth: 220 }}>
                                                {item.reason}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: 'space-between', bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                    <Button
                        onClick={() => setAuditModal({ open: false, issues: [], selectedIds: [] })}
                        disabled={isFixing}
                        sx={{ fontWeight: 800, color: '#64748b' }}
                    >
                        {isMr ? 'रद्द करा' : (isHi ? 'रद्द करें' : 'Cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleConfirmApplyFixes}
                        disabled={isFixing || auditModal.selectedIds.length === 0}
                        startIcon={<AutoFixHighIcon />}
                        sx={{ fontWeight: 900, borderRadius: 2.5, bgcolor: '#ea580c', px: 3, '&:hover': { bgcolor: '#c2410c' } }}
                    >
                        {isFixing
                            ? (isMr ? 'दुरुस्त करत आहे...' : (isHi ? 'दुरुस्त हो रहा है...' : 'Fixing...'))
                            : `${lbl.btnCorrectBills} (${auditModal.selectedIds.length})`
                        }
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Owner Delete Bill Confirmation Modal */}
            <Dialog
                open={deleteBillModal.open}
                onClose={() => !isDeletingBill && setDeleteBillModal({ open: false, order: null })}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 900, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DeleteIcon sx={{ color: '#dc2626' }} />
                    {isMr ? 'बिल डिलीट करा (Confirm Delete)' : (isHi ? 'बिल डिलीट करें' : 'Confirm Bill Deletion')}
                </DialogTitle>
                <DialogContent>
                    {deleteBillModal.order && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, my: 1 }}>
                            <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600 }}>
                                {isMr
                                    ? 'तुम्हाला खालील बिल डेटाबेसमधून कायमचे डिलीट करायचे आहे का?'
                                    : (isHi ? 'क्या आप इस बिल को डेटाबेस से हमेशा के लिए हटाना चाहते हैं?' : 'Are you sure you want to permanently delete this bill?')}
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a' }}>
                                    📄 {isMr ? 'बिल क्र:' : (isHi ? 'बिल नं:' : 'Bill ID:')} {deleteBillModal.order.id}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                                    📅 {isMr ? 'तारीख/वेळ:' : (isHi ? 'समय:' : 'Time:')} {(deleteBillModal.order.createdAt || deleteBillModal.order.created_at) ? formatDateDDMMYYYY(deleteBillModal.order.createdAt || deleteBillModal.order.created_at) + ' ' + parseDateSafe(deleteBillModal.order.createdAt || deleteBillModal.order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>
                                    🏷️ {isMr ? 'प्रकार:' : (isHi ? 'प्रकार:' : 'Type:')} {deleteBillModal.order.type || 'Table'} ({deleteBillModal.order.paymentMethod || deleteBillModal.order.payment_method || 'Cash'})
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 900, color: '#16a34a', mt: 1 }}>
                                    💰 {isMr ? 'एकूण रक्कम:' : (isHi ? 'कुल राशि:' : 'Total Amount:')} ₹{(deleteBillModal.order.total || 0).toFixed(0)}
                                </Typography>
                            </Paper>
                            <Alert severity="error" sx={{ borderRadius: 2, fontWeight: 700, fontSize: '0.85rem' }}>
                                ⚠️ {isMr
                                    ? 'हे बिल डेटाबेस आणि विक्री अहवालांमधून कायमचे नष्ट होईल. ही क्रिया परत घेता येणार नाही!'
                                    : (isHi ? 'यह बिल डेटाबेस और रिपोर्ट से हमेशा के लिए हट जाएगा। यह क्रिया वापस नहीं ली जा सकती!' : 'This bill will be permanently deleted from database & sales ledger. This action cannot be undone!')}
                            </Alert>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                    <Button
                        onClick={() => setDeleteBillModal({ open: false, order: null })}
                        disabled={isDeletingBill}
                        variant="outlined"
                        color="inherit"
                        sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}
                    >
                        {isMr ? 'रद्द करा (Cancel)' : (isHi ? 'रद्द करें' : 'Cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirmDeleteBill}
                        disabled={isDeletingBill}
                        variant="contained"
                        color="error"
                        startIcon={isDeletingBill ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
                        sx={{ borderRadius: 2, fontWeight: 900, textTransform: 'none', bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' } }}
                    >
                        {isDeletingBill ? (isMr ? 'डिलीट होत आहे...' : (isHi ? 'डिलीट हो रहा है...' : 'Deleting...')) : (isMr ? 'होय, बिल डिलीट करा' : (isHi ? 'हाँ, डिलीट करें' : 'Yes, Delete Bill'))}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Owner Force Settle Confirmation Modal */}
            <Dialog
                open={settleModal.open}
                onClose={() => !isForceSettling && setSettleModal({ open: false, order: null, paymentMethod: 'cash' })}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 900, color: '#16a34a', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ color: '#16a34a' }} />
                    {isMr ? 'बिल सेटल करा (Force Settle)' : (isHi ? 'बिल सेटल करें' : 'Force Settle Bill to Sales')}
                </DialogTitle>
                <DialogContent>
                    {settleModal.order && (
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" sx={{ color: '#475569', mb: 2, fontWeight: 500 }}>
                                {isMr
                                    ? `ऑर्डर #${settleModal.order.id} (${settleModal.order.type || 'Order'}) चा स्टेटस 'Served' (पूर्ण) करून विक्री अहवालात जमा केला जाईल.`
                                    : (isHi
                                        ? `ऑर्डर #${settleModal.order.id} का स्टेटस 'Served' करके बिक्री रिपोर्ट में जोड़ा जाएगा।`
                                        : `Order #${settleModal.order.id} will be marked as 'Served' and recorded in sales reports.`)}
                            </Typography>
                            <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2, mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>{lbl.colTotal}:</Typography>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#008751' }}>₹{Number(settleModal.order.total || 0).toFixed(0)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>{lbl.colWaiter}:</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 800, color: '#334155' }}>{settleModal.order.waiterName || 'Staff'}</Typography>
                                </Box>
                            </Paper>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={{ fontWeight: 700 }}>{isMr ? 'पेमेंट पद्धत निवडा' : (isHi ? 'भुगतान माध्यम चुनें' : 'Payment Method')}</InputLabel>
                                <Select
                                    value={settleModal.paymentMethod}
                                    label={isMr ? 'पेमेंट पद्धत निवडा' : (isHi ? 'भुगतान माध्यम चुनें' : 'Payment Method')}
                                    onChange={(e) => setSettleModal(prev => ({ ...prev, paymentMethod: e.target.value }))}
                                    sx={{ fontWeight: 800, borderRadius: 2 }}
                                >
                                    <MenuItem value="cash">💵 {isMr ? 'कॅश (Cash)' : (isHi ? 'नकद (Cash)' : 'Cash')}</MenuItem>
                                    <MenuItem value="upi">📱 {isMr ? 'यूपीआय / ऑनलाईन (UPI)' : (isHi ? 'यूपीआई (UPI)' : 'UPI / Online')}</MenuItem>
                                    <MenuItem value="zomato">🔴 {isMr ? 'झोमॅटो (Zomato)' : 'Zomato'}</MenuItem>
                                    <MenuItem value="swiggy">🟠 {isMr ? 'स्वीगी (Swiggy)' : 'Swiggy'}</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                    <Button
                        onClick={() => setSettleModal({ open: false, order: null, paymentMethod: 'cash' })}
                        disabled={isForceSettling}
                        variant="outlined"
                        color="inherit"
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                    >
                        {isMr ? 'रद्द करा (Cancel)' : (isHi ? 'रद्द करें' : 'Cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirmForceSettle}
                        disabled={isForceSettling}
                        variant="contained"
                        color="success"
                        startIcon={isForceSettling ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900, bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}
                    >
                        {isForceSettling ? '...' : (isMr ? 'होय, बिल सेटल करा' : (isHi ? 'हाँ, सेटल करें' : 'Confirm Settle'))}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
